import React, { useState } from 'react';
import { Container, Header, Segment, Button, Icon, Divider, List, Message, Grid, Label } from 'semantic-ui-react';

const AboutAndUserManual = ({ onClose, initialSection = 'about' }) => {
    const [activeSection, setActiveSection] = useState(initialSection);

    const sections = [
        { key: 'about', label: 'About Tenant Bridge', icon: 'info circle', color: 'blue' },
        { key: 'getting-started', label: 'Getting Started', icon: 'rocket', color: 'green' },
        { key: 'workflow', label: 'Request Workflow', icon: 'sitemap', color: 'violet' },
        { key: 'form-filler', label: 'User Guide (Requester)', icon: 'edit', color: 'teal' },
        { key: 'approver', label: 'User Guide (Approver)', icon: 'check circle', color: 'orange' },
        { key: 'license-capacity', label: 'License & Capacity Guide', icon: 'key', color: 'yellow' },
        { key: 'faq', label: 'FAQ', icon: 'question circle', color: 'grey' },
        { key: 'release-notes', label: 'Release Notes', icon: 'newspaper', color: 'pink' }
    ];

    const renderContent = () => {
        switch (activeSection) {
            case 'about':
                return (
                    <div>
                        <div style={{ 
                            marginBottom: '2rem',
                            paddingBottom: '1.5rem',
                            borderBottom: '2px solid rgba(53, 183, 244, 0.15)'
                        }}>
                            <Header as="h2" style={{ 
                                margin: 0, 
                                color: '#062D4D',
                                fontSize: '2rem',
                                fontWeight: '600',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                                lineHeight: '1.2'
                            }}>
                                <Icon name="info circle" style={{ flexShrink: 0, verticalAlign: 'middle' }} />
                                <span>About Tenant Bridge</span>
                            </Header>
                        </div>

                        {/* Quick Navigation Guide */}
                        <Message info style={{ 
                            borderRadius: '8px',
                            border: '1px solid rgba(53, 183, 244, 0.2)',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                            marginBottom: '2rem',
                            padding: '1.25rem'
                        }}>
                            <Message.Header style={{ fontWeight: '600', fontSize: '1rem', marginBottom: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <Icon name="compass" />
                                How to Use This Manual
                            </Message.Header>
                            <p style={{ marginTop: '0.5rem', marginBottom: '0.75rem', lineHeight: '1.7', fontSize: '0.95rem' }}>
                                This manual is organized into <strong>8 sections</strong>, accessible from the <strong>Table of Contents</strong> sidebar on the left:
                            </p>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '0.75rem', marginTop: '0.75rem' }}>
                                {sections.map((section, idx) => (
                                    <div key={section.key} style={{
                                        padding: '0.75rem',
                                        background: '#ffffff',
                                        borderRadius: '6px',
                                        border: '1px solid rgba(0,0,0,0.08)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.5rem',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease'
                                    }}
                                    onClick={() => setActiveSection(section.key)}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.background = '#f8f9fa';
                                        e.currentTarget.style.borderColor = section.color === 'blue' ? '#062D4D' : section.color === 'green' ? '#6F9911' : section.color === 'teal' ? '#00b5ad' : section.color === 'orange' ? '#f2711c' : section.color === 'yellow' ? '#daa520' : section.color === 'purple' ? '#a333c8' : section.color === 'violet' ? '#6435c9' : section.color === 'pink' ? '#e03997' : '#767676';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.background = '#ffffff';
                                        e.currentTarget.style.borderColor = 'rgba(0,0,0,0.08)';
                                    }}
                                    >
                                        <Icon 
                                            name={section.icon} 
                                            style={{ 
                                                color: section.color === 'blue' ? '#062D4D' : section.color === 'green' ? '#6F9911' : section.color === 'teal' ? '#00b5ad' : section.color === 'orange' ? '#f2711c' : section.color === 'yellow' ? '#daa520' : section.color === 'purple' ? '#a333c8' : section.color === 'violet' ? '#6435c9' : section.color === 'pink' ? '#e03997' : '#767676',
                                                fontSize: '1.1em'
                                            }} 
                                        />
                                        <span style={{ fontSize: '0.9rem', fontWeight: '500' }}>{section.label}</span>
                                    </div>
                                ))}
                            </div>
                            <p style={{ marginTop: '1rem', marginBottom: 0, fontSize: '0.9rem', color: '#666', fontStyle: 'italic' }}>
                                💡 <strong>Tip:</strong> Click any section name above or use the sidebar to navigate. Each section contains detailed information specific to that topic.
                            </p>
                        </Message>
                        
                        <div style={{ 
                            fontSize: '1.1em', 
                            lineHeight: '1.8', 
                            marginBottom: '2rem',
                            color: '#062D4D',
                            padding: '1.5rem',
                            background: '#ffffff',
                            borderRadius: '8px',
                            border: '1px solid rgba(0,0,0,0.06)',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                        }}>
                            <strong style={{ color: '#062D4D', fontSize: '1.15em' }}>Tenant Bridge</strong> is a comprehensive cloud infrastructure provisioning platform that streamlines the end-to-end process of requesting, approving, and provisioning virtual machines (VMs) on Azure and Linode.
                        </div>
                        
                        <div style={{ marginBottom: '2rem' }}>
                            <Header as="h3" style={{ 
                                marginBottom: '1rem',
                                color: '#062D4D',
                                fontSize: '1.4rem',
                                fontWeight: '600',
                                paddingBottom: '0.5rem',
                                borderBottom: '1px solid rgba(0,0,0,0.08)'
                            }}>
                                Key Features
                            </Header>
                            <div style={{
                                padding: '1.25rem',
                                background: '#ffffff',
                                borderRadius: '8px',
                                border: '1px solid rgba(0,0,0,0.06)',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                            }}>
                                <style>{`
                                    .key-features-list.ui.list {
                                        margin-left: 1.5rem !important;
                                        padding-left: 0 !important;
                                    }
                                    .key-features-list.ui.list > .item {
                                        padding-left: 0.5rem !important;
                                        padding-right: 0 !important;
                                        margin-left: 0 !important;
                                        display: list-item !important;
                                        list-style-type: disc !important;
                                        list-style-position: outside !important;
                                    }
                                    .key-features-list.ui.list > .item::before {
                                        display: none !important;
                                    }
                                `}</style>
                                <List bulleted relaxed className="key-features-list" style={{ 
                                    margin: 0, 
                                    marginLeft: '1.5rem'
                                }}>
                                    <List.Item style={{ 
                                        padding: '0.5rem 0 0.5rem 0.5rem', 
                                        fontSize: '0.95rem', 
                                        lineHeight: '1.7',
                                        marginLeft: 0
                                    }}>
                                        <strong>Enhanced Database Performance:</strong> Robust backend built for improved performance, reliability, and scalability
                                    </List.Item>
                                    <List.Item style={{ 
                                        padding: '0.5rem 0 0.5rem 0.5rem', 
                                        fontSize: '0.95rem', 
                                        lineHeight: '1.7',
                                        marginLeft: 0
                                    }}>
                                        <strong>Real-Time Notifications:</strong> Instant browser and in-app notifications for request updates and approvals
                                    </List.Item>
                                    <List.Item style={{ 
                                        padding: '0.5rem 0 0.5rem 0.5rem', 
                                        fontSize: '0.95rem', 
                                        lineHeight: '1.7',
                                        marginLeft: 0
                                    }}>
                                        <strong>Advanced Search & Filtering:</strong> Global search, multi-select filters, date ranges, and saved filter presets
                                    </List.Item>
                                    <List.Item style={{ 
                                        padding: '0.5rem 0 0.5rem 0.5rem', 
                                        fontSize: '0.95rem', 
                                        lineHeight: '1.7',
                                        marginLeft: 0
                                    }}>
                                        <strong>Search Term Highlighting:</strong> Visual highlighting of search terms in table results for easy identification
                                    </List.Item>
                                    <List.Item style={{ 
                                        padding: '0.5rem 0 0.5rem 0.5rem', 
                                        fontSize: '0.95rem', 
                                        lineHeight: '1.7',
                                        marginLeft: 0
                                    }}>
                                        <strong>Role-Based Request Submission:</strong> Submit VM requests via the Single Tenancy Form or Multi Tenancy Form based on your assigned role
                                    </List.Item>
                                    <List.Item style={{ 
                                        padding: '0.5rem 0 0.5rem 0.5rem', 
                                        fontSize: '0.95rem', 
                                        lineHeight: '1.7',
                                        marginLeft: 0
                                    }}>
                                        <strong>Streamlined Approval Workflow:</strong> Structured request flow from User to Approver
                                    </List.Item>
                                    <List.Item style={{ 
                                        padding: '0.5rem 0 0.5rem 0.5rem', 
                                        fontSize: '0.95rem', 
                                        lineHeight: '1.7',
                                        marginLeft: 0
                                    }}>
                                        <strong>Automated VM Provisioning:</strong> One-click provisioning on Azure and Linode cloud platforms
                                    </List.Item>
                                    <List.Item style={{ 
                                        padding: '0.5rem 0 0.5rem 0.5rem', 
                                        fontSize: '0.95rem', 
                                        lineHeight: '1.7',
                                        marginLeft: 0
                                    }}>
                                        <strong>Request Tracking & History:</strong> Comprehensive tracking and history with advanced filtering
                                    </List.Item>
                                    <List.Item style={{ 
                                        padding: '0.5rem 0 0.5rem 0.5rem', 
                                        fontSize: '0.95rem', 
                                        lineHeight: '1.7',
                                        marginLeft: 0
                                    }}>
                                        <strong>Role-Based Dashboards:</strong> Tailored views and actions for each user type
                                    </List.Item>
                                    <List.Item style={{ 
                                        padding: '0.5rem 0 0.5rem 0.5rem', 
                                        fontSize: '0.95rem', 
                                        lineHeight: '1.7',
                                        marginLeft: 0
                                    }}>
                                        <strong>Email & Real-Time Notifications:</strong> Stay informed with instant updates at every stage of a request
                                    </List.Item>
                                    <List.Item style={{ 
                                        padding: '0.5rem 0 0.5rem 0.5rem', 
                                        fontSize: '0.95rem', 
                                        lineHeight: '1.7',
                                        marginLeft: 0
                                    }}>
                                        <strong>Multi-Environment Support:</strong> Manage requests across DEV, QA, STAGE, and PROD environments
                                    </List.Item>
                                    <List.Item style={{ 
                                        padding: '0.5rem 0 0.5rem 0.5rem', 
                                        fontSize: '0.95rem', 
                                        lineHeight: '1.7',
                                        marginLeft: 0
                                    }}>
                                        <strong>All Events View:</strong> Centralized logging and monitoring of all application activities
                                    </List.Item>
                                    <List.Item style={{ 
                                        padding: '0.5rem 0 0.5rem 0.5rem', 
                                        fontSize: '0.95rem', 
                                        lineHeight: '1.7',
                                        marginLeft: 0
                                    }}>
                                        <strong>Auto Provisioning:</strong> Streamlined VM creation for pre-approved categories without manual approver intervention
                                    </List.Item>
                                    <List.Item style={{ 
                                        padding: '0.5rem 0 0.5rem 0.5rem', 
                                        fontSize: '0.95rem', 
                                        lineHeight: '1.7',
                                        marginLeft: 0
                                    }}>
                                        <strong>Timezone Support:</strong> User-configurable timezone preferences for personalized date/time display
                                    </List.Item>
                                    <List.Item style={{ 
                                        padding: '0.5rem 0 0.5rem 0.5rem', 
                                        fontSize: '0.95rem', 
                                        lineHeight: '1.7',
                                        marginLeft: 0
                                    }}>
                                        <strong>Migration Support:</strong> Single tenant to multi-tenant migration workflow with DevOps approval
                                    </List.Item>
                                    <List.Item style={{ 
                                        padding: '0.5rem 0 0.5rem 0.5rem', 
                                        fontSize: '0.95rem', 
                                        lineHeight: '1.7',
                                        marginLeft: 0
                                    }}>
                                        <strong>Linode Integration:</strong> Full support for Linode instance provisioning with account selection (POC, DEVQA, CUST)
                                    </List.Item>
                                </List>
                            </div>
                        </div>

                        <div style={{ marginBottom: '2rem' }}>
                            <Header as="h3" style={{ 
                                marginBottom: '1rem',
                                color: '#062D4D',
                                fontSize: '1.4rem',
                                fontWeight: '600',
                                paddingBottom: '0.5rem',
                                borderBottom: '1px solid rgba(0,0,0,0.08)'
                            }}>
                                Purpose
                            </Header>
                            <div style={{
                                padding: '1.25rem',
                                background: '#ffffff',
                                borderRadius: '8px',
                                border: '1px solid rgba(0,0,0,0.06)',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                                fontSize: '0.95rem',
                                lineHeight: '1.7',
                                color: '#062D4D'
                            }}>
                                tenantBridge eliminates manual processes and significantly reduces the time from request to deployment. 
                                It enforces proper approval workflows, maintains comprehensive audit trails, and provides a 
                                centralized platform for managing cloud infrastructure requests across your organization.
                            </div>
                        </div>

                        <Message info style={{ 
                            borderRadius: '8px',
                            border: '1px solid rgba(53, 183, 244, 0.2)',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                        }}>
                            <Message.Header style={{ fontWeight: '600' }}>Version Information</Message.Header>
                            <div style={{ marginTop: '0.5rem' }}>
                                <p style={{ margin: '0.25rem 0' }}><strong>Current Version:</strong> v3.1.1</p>
                                <p style={{ margin: '0.25rem 0' }}><strong>Last Updated:</strong> TBD </p>
                            </div>
                        </Message>
                    </div>
                );

            case 'getting-started':
                return (
                    <div>
                        <div style={{ 
                            marginBottom: '2rem',
                            paddingBottom: '1.5rem',
                            borderBottom: '2px solid rgba(33, 181, 83, 0.15)'
                        }}>
                            <Header as="h2" style={{ 
                                margin: 0, 
                                color: '#6F9911',
                                fontSize: '2rem',
                                fontWeight: '600',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                                lineHeight: '1.2'
                            }}>
                                <Icon name="rocket" style={{ flexShrink: 0, verticalAlign: 'middle' }} />
                                <span>Getting Started</span>
                            </Header>
                        </div>

                        {/* Welcome Message for New Users */}
                        <Message info style={{ 
                            borderRadius: '8px',
                            border: '1px solid rgba(53, 183, 244, 0.2)',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                            marginBottom: '2rem',
                            padding: '1.5rem'
                        }}>
                            <Message.Header style={{ fontWeight: '600', fontSize: '1.1rem', marginBottom: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <Icon name="info circle" style={{ flexShrink: 0, verticalAlign: 'middle' }} />
                                <span>Welcome to tenantBridge!</span>
                            </Message.Header>
                            <p style={{ marginTop: '0.5rem', marginBottom: '0.75rem', lineHeight: '1.7' }}>
                                This user manual is organized into clearly defined sections. Use the <strong>Table of Contents</strong> on the left to navigate:
                            </p>
                            <List bulleted style={{ marginLeft: '1.5rem', marginTop: '0.5rem' }}>
                                <List.Item style={{ padding: '0.3rem 0', fontSize: '0.95rem', lineHeight: '1.6' }}>
                                    <strong>About Tenant Bridge</strong> — Overview of tenantBridge and its key features
                                </List.Item>
                                <List.Item style={{ padding: '0.3rem 0', fontSize: '0.95rem', lineHeight: '1.6' }}>
                                    <strong>Getting Started</strong> — Quick-start guide (you are here)
                                </List.Item>
                                <List.Item style={{ padding: '0.3rem 0', fontSize: '0.95rem', lineHeight: '1.6' }}>
                                    <strong>Request Workflow</strong> — End-to-end process flow with visual diagrams
                                </List.Item>
                                <List.Item style={{ padding: '0.3rem 0', fontSize: '0.95rem', lineHeight: '1.6' }}>
                                    <strong>Role Guides</strong> — Detailed guides for Requesters and Approvers
                                </List.Item>
                                <List.Item style={{ padding: '0.3rem 0', fontSize: '0.95rem', lineHeight: '1.6' }}>
                                    <strong>FAQ</strong> — Frequently asked questions
                                </List.Item>
                                <List.Item style={{ padding: '0.3rem 0', fontSize: '0.95rem', lineHeight: '1.6' }}>
                                    <strong>Release Notes</strong> — Latest features and updates
                                </List.Item>
                            </List>
                        </Message>
                        
                        {[
                            {
                                title: 'Step 1: Sign In',
                                content: 'Open tenantBridge and sign in with your CrashPlan Google account. Click the "Sign In" button on the login page to authenticate.'
                            },
                            {
                                title: 'Step 2: Understand Your Role',
                                content: 'Your level of access and available actions depend on your assigned role:',
                                list: [
                                    { label: 'SINGLE TENANCY FORM', desc: 'Submit VM requests using the standard single-tenancy form' },
                                    { label: 'MULTI TENANCY FORM', desc: 'Submit VM requests using the enhanced multi-tenancy form' },
                                    { label: 'APPROVER', desc: 'Review, approve, or reject requests and trigger VM provisioning' },
                                ]
                            },
                            {
                                title: 'Step 3: Navigate the Interface',
                                content: 'The main interface is composed of the following areas:',
                                list: [
                                    { label: 'Navbar', desc: 'Top navigation bar with your profile, preferences, and notifications' },
                                    { label: 'Tabs', desc: 'Role-specific tabs for accessing different functions' },
                                    { label: 'Request Forms', desc: 'Submit new VM provisioning requests' },
                                    { label: 'History Views', desc: 'Track and manage your requests and approvals' }
                                ]
                            }
                        ].map((step, idx) => (
                            <div key={idx} style={{ 
                                marginBottom: '1.5rem',
                                padding: '1.75rem',
                                background: '#ffffff',
                                borderRadius: '8px',
                                border: '1px solid rgba(0,0,0,0.06)',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                                borderLeft: `4px solid ${idx === 0 ? '#6F9911' : idx === 1 ? '#062D4D' : '#f2711c'}`
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
                                    <div style={{
                                        width: '32px',
                                        height: '32px',
                                        borderRadius: '50%',
                                        background: `linear-gradient(135deg, ${idx === 0 ? '#6F9911' : idx === 1 ? '#062D4D' : '#f2711c'} 0%, ${idx === 0 ? '#5a7a0e' : idx === 1 ? '#062D4D' : '#d85f0a'} 100%)`,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: '#ffffff',
                                        fontWeight: '700',
                                        fontSize: '1rem',
                                        flexShrink: 0
                                    }}>
                                        {idx + 1}
                                    </div>
                                <Header as="h3" style={{ 
                                        margin: 0,
                                    color: '#062D4D',
                                        fontSize: '1.3rem',
                                    fontWeight: '600'
                                }}>
                                    {step.title}
                                </Header>
                                </div>
                                <p style={{ marginBottom: step.list ? '1rem' : 0, color: '#062D4D', lineHeight: '1.7' }}>
                                    {step.content}
                                </p>
                                {step.list && (
                                    <List bulleted relaxed style={{ marginTop: '0.5rem' }}>
                                        {step.list.map((item, i) => (
                                            <List.Item key={i} style={{ padding: '0.4rem 0', fontSize: '0.95rem', lineHeight: '1.6' }}>
                                                <strong style={{ color: '#062D4D' }}>{item.label}</strong>: {item.desc}
                                            </List.Item>
                                        ))}
                                    </List>
                                )}
                            </div>
                        ))}

                    </div>
                );

            case 'form-filler':
                return (
                    <div>
                        <div style={{ 
                            marginBottom: '2rem',
                            paddingBottom: '1.5rem',
                            borderBottom: '2px solid rgba(0, 181, 173, 0.15)'
                        }}>
                            <Header as="h2" style={{ 
                                margin: 0, 
                                color: '#00b5ad',
                                fontSize: '2rem',
                                fontWeight: '600',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                                lineHeight: '1.2'
                            }}>
                                <Icon name="edit" style={{ flexShrink: 0, verticalAlign: 'middle' }} />
                                <span>User Guide (Requester)</span>
                            </Header>
                        </div>

                        <div style={{ marginBottom: '2rem' }}>
                            <Header as="h3" style={{ 
                                marginBottom: '1rem',
                                color: '#062D4D',
                                fontSize: '1.4rem',
                                fontWeight: '600',
                                paddingBottom: '0.5rem',
                                borderBottom: '1px solid rgba(0,0,0,0.08)'
                            }}>
                                Submitting a Request
                            </Header>
                            <div style={{
                                padding: '1.5rem',
                                background: '#ffffff',
                                borderRadius: '8px',
                                border: '1px solid rgba(0,0,0,0.06)',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                                marginBottom: '1rem',
                                transition: 'box-shadow 0.2s ease'
                            }}>
                                <p style={{ marginBottom: '1.5rem', color: '#062D4D', lineHeight: '1.7' }}>
                                    As a requester, you can submit VM provisioning requests using the form that matches your assigned role — either the Single Tenancy Form or the Multi Tenancy Form.
                                </p>
                                
                                <div style={{ marginBottom: '1.5rem' }}>
                                    <Header as="h4" style={{ 
                                        marginTop: 0,
                                        marginBottom: '0.75rem',
                                        color: '#062D4D',
                                        fontSize: '1.1rem',
                                        fontWeight: '600'
                                    }}>
                                        Single Tenancy Form
                                    </Header>
                                    <List ordered relaxed style={{ marginLeft: '1.5rem' }}>
                                        {[
                                    'Navigate to the "Single Tenancy Form" tab',
                                    'Select your Category from the dropdown (DEV, QA, STAGE, or PROD)',
                                    'Select your Tenant from the dropdown (*.parablu.com or *.crashplan.com)',
                                    'Choose the Hosting Location (varies by category)',
                                    'Select the Hosting Type (Single Tenancy Setup or Multi Tenancy Setup)',
                                    'Select Cloud Provider: Azure or Linode (if available for your category)',
                                    'Toggle "Is this setup being created for migrating single tenancy to v2.6.x?" if applicable',
                                    'Enter your desired URL name in the "Decide your URL" field',
                                    'Review your information and click "Submit Request"'
                                        ].map((item, i) => (
                                            <List.Item key={i} style={{ padding: '0.4rem 0', fontSize: '0.95rem', lineHeight: '1.6' }}>
                                                {item}
                                            </List.Item>
                                        ))}
                                    </List>
                                </div>

                                <Divider style={{ margin: '1.5rem 0' }} />

                                <div>
                                    <Header as="h4" style={{ 
                                        marginTop: 0,
                                        marginBottom: '0.75rem',
                                        color: '#062D4D',
                                        fontSize: '1.1rem',
                                        fontWeight: '600'
                                    }}>
                                        Multi Tenancy Form
                                    </Header>
                                    <List ordered relaxed style={{ marginLeft: '1.5rem' }}>
                                        {[
                                    'Navigate to the "Multi Tenancy Form" tab',
                                    'Select your Category from the dropdown (ENGG_DEV, ENGG_QA, STAGE, or PROD)',
                                    'Note: Tenant is automatically set based on your category selection (*.parablu.com for DEV/QA, *.crashplan.com for STAGE/PROD)',
                                    'Select the Hosting Location (location options vary by category)',
                                    'Select Storage Destination: Azure Blob, CPG DataCenter, or BYOS (S3-Compatible)',
                                    'If category is PROD: Check "Is this tenant for POC?" if applicable',
                                    'Toggle "Single Tenant to Multi Tenant Migration?" if this is a migration request',
                                    'If migration is enabled: Enter your old tenant URL (cloudName field is cleared)',
                                    'If migration is enabled: Optionally set "Migration to be completed at or before" date (defaults to Feb 25, 2027 13:00 if not set)',
                                    'If migration is disabled: Enter a unique Customer Name in the "Decide your URL" field',
                                    'Note: Migration requests require DevOps approval',
                                    'Review all your entries and click "Submit Request"'
                                        ].map((item, i) => (
                                            <List.Item key={i} style={{ padding: '0.4rem 0', fontSize: '0.95rem', lineHeight: '1.6' }}>
                                                {item}
                                            </List.Item>
                                        ))}
                                    </List>
                                </div>
                            </div>
                        </div>

                        <div style={{ marginBottom: '2rem' }}>
                            <Header as="h3" style={{ 
                                marginBottom: '1rem',
                                color: '#062D4D',
                                fontSize: '1.4rem',
                                fontWeight: '600',
                                paddingBottom: '0.5rem',
                                borderBottom: '1px solid rgba(0,0,0,0.08)'
                            }}>
                                Tracking Your Requests
                            </Header>
                            <div style={{
                                padding: '1.5rem',
                                background: '#ffffff',
                                borderRadius: '8px',
                                border: '1px solid rgba(0,0,0,0.06)',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                            }}>
                                <p style={{ marginBottom: '1rem', color: '#062D4D', lineHeight: '1.7' }}>
                                    After submitting a request, you can monitor its progress in the "My Submitted Requests" tab:
                                </p>
                                <List bulleted relaxed style={{ marginLeft: '1.5rem' }}>
                                    {[
                                        'View all requests you have submitted',
                                        'Check the current status of each request (e.g., PENDING_APPROVER, APPROVED, REJECTED)',
                                        'Search and filter your requests using the built-in filters',
                                        'View detailed request information and approval history',
                                        'Edit or withdraw requests that are still pending approval',
                                        'Access credentials for completed requests via Keeper or JIRA (see below)'
                                    ].map((item, i) => (
                                        <List.Item key={i} style={{ padding: '0.4rem 0', fontSize: '0.95rem', lineHeight: '1.6' }}>
                                            {item}
                                        </List.Item>
                                    ))}
                                </List>
                            </div>
                        </div>

                        <div style={{ marginBottom: '2rem' }}>
                            <Header as="h3" style={{ 
                                marginBottom: '1rem',
                                color: '#062D4D',
                                fontSize: '1.4rem',
                                fontWeight: '600',
                                paddingBottom: '0.5rem',
                                borderBottom: '1px solid rgba(0,0,0,0.08)'
                            }}>
                                Accessing Credentials (JIRA & Keeper)
                            </Header>
                            <div style={{
                                padding: '1.5rem',
                                background: '#ffffff',
                                borderRadius: '8px',
                                border: '1px solid rgba(0,0,0,0.06)',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                            }}>
                                <p style={{ marginBottom: '1rem', color: '#062D4D', lineHeight: '1.7' }}>
                                    Once your request has been successfully provisioned, credential access buttons appear in the <strong>Actions</strong> column of the "My Submitted Requests" table. The specific button shown depends on the request category and form type:
                                </p>

                                <div style={{ marginBottom: '1.25rem' }}>
                                    <Header as="h4" style={{ 
                                        marginTop: 0,
                                        marginBottom: '0.75rem',
                                        color: '#062D4D',
                                        fontSize: '1.1rem',
                                        fontWeight: '600'
                                    }}>
                                        <Icon name="ticket" style={{ color: '#2185d0' }} />
                                        Request Credentials via JIRA
                                    </Header>
                                    <p style={{ marginBottom: '0.75rem', color: '#062D4D', lineHeight: '1.7', marginLeft: '1.5rem' }}>
                                        A blue <strong>"Request Credentials via JIRA"</strong> button appears for the following scenarios:
                                    </p>
                                    <List bulleted relaxed style={{ marginLeft: '3rem' }}>
                                        {[
                                            'PROD or CUST category requests submitted via the Single Tenancy Form',
                                            'PROD category requests submitted via the Multi Tenancy Form where the request is NOT a POC'
                                        ].map((item, i) => (
                                            <List.Item key={i} style={{ padding: '0.3rem 0', fontSize: '0.95rem', lineHeight: '1.6' }}>{item}</List.Item>
                                        ))}
                                    </List>
                                    <p style={{ marginTop: '0.75rem', color: '#555', lineHeight: '1.7', marginLeft: '1.5rem', fontSize: '0.95rem' }}>
                                        Clicking this button opens a new tab with a pre-filled JIRA issue creation page. The issue summary and description are auto-populated with your request details (Request ID, Cloud Name, Submitted By, and Status). You can then add any additional context and submit the ticket to the DevOps team.
                                    </p>
                                </div>

                                <Divider style={{ margin: '1.25rem 0' }} />

                                <div style={{ marginBottom: '0.5rem' }}>
                                    <Header as="h4" style={{ 
                                        marginTop: 0,
                                        marginBottom: '0.75rem',
                                        color: '#062D4D',
                                        fontSize: '1.1rem',
                                        fontWeight: '600'
                                    }}>
                                        <Icon name="key" style={{ color: '#21ba45' }} />
                                        Access Credentials via Keeper
                                    </Header>
                                    <p style={{ marginBottom: '0.75rem', color: '#062D4D', lineHeight: '1.7', marginLeft: '1.5rem' }}>
                                        A green <strong>"Access Credentials via Keeper"</strong> button appears for all other completed requests:
                                    </p>
                                    <List bulleted relaxed style={{ marginLeft: '3rem' }}>
                                        {[
                                            'All categories other than PROD and CUST (e.g., STAGE, ENGG_DEV, ENGG_QA, DEVOPS, DEMO, TESTING, POC)',
                                            'PROD category via Multi Tenancy Form where the request IS a POC',
                                            'CUST category via Multi Tenancy Form'
                                        ].map((item, i) => (
                                            <List.Item key={i} style={{ padding: '0.3rem 0', fontSize: '0.95rem', lineHeight: '1.6' }}>{item}</List.Item>
                                        ))}
                                    </List>
                                    <p style={{ marginTop: '0.75rem', color: '#555', lineHeight: '1.7', marginLeft: '1.5rem', fontSize: '0.95rem' }}>
                                        Clicking this button opens the Keeper Security vault in a new tab, where your VM/instance credentials are securely stored. You can find your credentials by searching for the cloud name or request ID within Keeper.
                                    </p>
                                </div>

                                <Message info style={{ 
                                    borderRadius: '8px',
                                    border: '1px solid rgba(53, 183, 244, 0.2)',
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                                    marginTop: '1.25rem'
                                }}>
                                    <Message.Header style={{ fontWeight: '600' }}>When do these buttons appear?</Message.Header>
                                    <p style={{ marginTop: '0.5rem', marginBottom: 0, lineHeight: '1.7' }}>
                                        These credential access buttons only appear <strong>after a request has been successfully provisioned</strong> (status: COMPLETED, TENANT IS READY, or MULTI TENANCY FORM COMPLETED). They replace the Withdraw and Edit buttons that were previously available while the request was pending.
                                    </p>
                                </Message>
                            </div>
                        </div>

                        <div style={{ marginBottom: '2rem' }}>
                            <Header as="h3" style={{ 
                                marginBottom: '1rem',
                                color: '#062D4D',
                                fontSize: '1.4rem',
                                fontWeight: '600',
                                paddingBottom: '0.5rem',
                                borderBottom: '1px solid rgba(0,0,0,0.08)'
                            }}>
                                Email Notifications
                            </Header>
                            <div style={{
                                padding: '1.5rem',
                                background: '#ffffff',
                                borderRadius: '8px',
                                border: '1px solid rgba(0,0,0,0.06)',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                            }}>
                                <p style={{ marginBottom: '1rem', color: '#062D4D', lineHeight: '1.7' }}>
                                    tenantBridge sends automated email notifications at every key stage of the request lifecycle. Below is a summary of the emails you will receive:
                                </p>
                                <List bulleted relaxed style={{ marginLeft: '1.5rem' }}>
                                    {[
                                        { label: 'Submission Confirmation', desc: 'Sent immediately after you submit a request, confirming that your request has been received.' },
                                        { label: 'Approval / Rejection', desc: 'Notifies you when an approver approves or rejects your request, along with any comments or reasons provided.' },
                                        { label: 'Provisioning Completion', desc: 'Sent once your VM or instance has been successfully provisioned. Includes the portal URL, cloud name, and credential access details.' },
                                        { label: 'Provisioning Error', desc: 'Sent if VM or instance creation fails, including error details and recommended next steps.' },
                                        { label: 'Withdrawal Confirmation', desc: 'Confirms that your request has been withdrawn. Sent to both you and the relevant approvers.' },
                                        { label: 'Account Deletion', desc: 'If you delete your account from Settings, a confirmation email is sent to you and a notification is sent to the DevOps team.' }
                                    ].map((item, i) => (
                                        <List.Item key={i} style={{ padding: '0.4rem 0', fontSize: '0.95rem', lineHeight: '1.6' }}>
                                            <strong style={{ color: '#062D4D' }}>{item.label}:</strong> {item.desc}
                                        </List.Item>
                                    ))}
                                </List>
                                <p style={{ marginTop: '1rem', color: '#555', lineHeight: '1.7', fontSize: '0.95rem' }}>
                                    All emails are sent from <strong>tenantBridge</strong> and include your request details for easy reference. Approvers and DevOps team members receive their own relevant notifications as well.
                                </p>
                            </div>
                        </div>

                        <div style={{ marginBottom: '2rem' }}>
                            <Header as="h3" style={{ 
                                marginBottom: '1rem',
                                color: '#DC2626',
                                fontSize: '1.4rem',
                                fontWeight: '600',
                                paddingBottom: '0.5rem',
                                borderBottom: '1px solid rgba(220, 38, 38, 0.15)'
                            }}>
                                Delete Account
                            </Header>
                            <div style={{
                                padding: '1.5rem',
                                background: '#ffffff',
                                borderRadius: '8px',
                                border: '1px solid rgba(220, 38, 38, 0.15)',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                            }}>
                                <p style={{ marginBottom: '1rem', color: '#062D4D', lineHeight: '1.7' }}>
                                    You can permanently delete your account from the <strong>Settings → Account</strong> tab. Please note that this action is irreversible.
                                </p>

                                <div style={{ marginBottom: '1rem' }}>
                                    <Header as="h4" style={{ 
                                        marginTop: 0,
                                        marginBottom: '0.75rem',
                                        color: '#062D4D',
                                        fontSize: '1.1rem',
                                        fontWeight: '600'
                                    }}>
                                        How to Delete Your Account
                                    </Header>
                                    <List ordered relaxed style={{ marginLeft: '1.5rem' }}>
                                        {[
                                            'Navigate to Settings by clicking the gear icon (⚙️) in the navbar dropdown menu',
                                            'Go to the "Account" tab',
                                            'Scroll down to the "Danger Zone" section at the bottom',
                                            'Click the "Delete My Account" button (red)',
                                            'A confirmation modal will appear — type DELETE (in uppercase) in the text field to confirm',
                                            'Click "Permanently Delete Account" to proceed',
                                            'You will be logged out immediately after deletion'
                                        ].map((item, i) => (
                                            <List.Item key={i} style={{ padding: '0.4rem 0', fontSize: '0.95rem', lineHeight: '1.6' }}>{item}</List.Item>
                                        ))}
                                    </List>
                                </div>

                                <div style={{ marginBottom: '1rem' }}>
                                    <Header as="h4" style={{ 
                                        marginTop: 0,
                                        marginBottom: '0.75rem',
                                        color: '#062D4D',
                                        fontSize: '1.1rem',
                                        fontWeight: '600'
                                    }}>
                                        What Happens When You Delete Your Account
                                    </Header>
                                    <List bulleted relaxed style={{ marginLeft: '1.5rem' }}>
                                        {[
                                            'Your account is permanently removed from the system (admin panel)',
                                            'You lose access to tenantBridge immediately',
                                            'A confirmation email is sent to your email address',
                                            'A notification email is sent to all DevOps team members',
                                            'You are logged out and redirected to the login page'
                                        ].map((item, i) => (
                                            <List.Item key={i} style={{ padding: '0.3rem 0', fontSize: '0.95rem', lineHeight: '1.6' }}>{item}</List.Item>
                                        ))}
                                    </List>
                                </div>

                                <Message warning style={{ 
                                    borderRadius: '8px',
                                    border: '1px solid rgba(242, 113, 28, 0.2)',
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                                    marginTop: '1rem'
                                }}>
                                    <Message.Header style={{ fontWeight: '600' }}>Important</Message.Header>
                                    <p style={{ marginTop: '0.5rem', marginBottom: 0, lineHeight: '1.7' }}>
                                        Account deletion is <strong>permanent and irreversible</strong>. If you deleted your account by mistake, 
                                        contact the tenantBridge team immediately to request restoration. A DevOps administrator can re-add your account from the Admin Panel.
                                    </p>
                                </Message>
                            </div>
                        </div>

                        <div style={{ marginBottom: '2rem' }}>
                            <Header as="h3" style={{ 
                                marginBottom: '1rem',
                                color: '#062D4D',
                                fontSize: '1.4rem',
                                fontWeight: '600',
                                paddingBottom: '0.5rem',
                                borderBottom: '1px solid rgba(0,0,0,0.08)'
                            }}>
                                Request Statuses
                            </Header>
                            <div style={{
                                padding: '1.5rem',
                                background: '#ffffff',
                                borderRadius: '8px',
                                border: '1px solid rgba(0,0,0,0.06)',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                            }}>
                                <List bulleted relaxed style={{ marginLeft: '1.5rem' }}>
                                    {[
                                        { status: 'PENDING_APPROVER', desc: 'Waiting for approver approval' },
                                        { status: 'APPROVED', desc: 'Approved and VM provisioning initiated' },
                                        { status: 'PROVISIONING', desc: 'VM/Instance is being created on cloud platform (Azure or Linode)' },
                                        { status: 'COMPLETED', desc: 'VM/Instance successfully provisioned and configured' },
                                        { status: 'TENANT IS READY', desc: 'Multi-tenancy setup completed and tenant is ready for use' },
                                        { status: 'MIGRATION IS IN PROGRESS', desc: 'Single tenant to multi-tenant migration is currently in progress' },
                                        { status: 'MULTI TENANCY FORM COMPLETED', desc: 'Multi Tenancy Form workflow completed successfully' },
                                        { status: 'REJECTED', desc: 'Request was rejected by approver' },
                                        { status: 'WITHDRAWN', desc: 'Request was withdrawn by submitter' },
                                        { status: 'ERROR', desc: 'An error occurred during provisioning' },
                                        { status: 'FAILED-VM Creation', desc: 'VM creation failed on cloud platform' },
                                        { status: 'FAILED-Setup', desc: 'Post-provisioning setup failed' },
                                        { status: 'FAILED-Multi Tenancy Form Script', desc: 'Multi Tenancy Form script execution failed' },
                                        { status: 'FAILED-Multi Tenancy Form Processing', desc: 'Multi Tenancy Form processing failed' },
                                        { status: 'VM_CREATION_CANCELLED', desc: 'VM creation was cancelled by approver' },
                                        { status: 'LINODE_INSTANCE_CREATION_CANCELLED', desc: 'Linode instance creation was cancelled by approver' }
                                    ].map((item, i) => (
                                        <List.Item key={i} style={{ padding: '0.4rem 0', fontSize: '0.95rem', lineHeight: '1.6' }}>
                                            <strong style={{ color: '#062D4D' }}>{item.status}</strong>: {item.desc}
                                        </List.Item>
                                    ))}
                                </List>
                            </div>
                        </div>

                        <div style={{ marginBottom: '2rem' }}>
                            <Header as="h3" style={{ 
                                marginBottom: '1rem',
                                color: '#062D4D',
                                fontSize: '1.4rem',
                                fontWeight: '600',
                                paddingBottom: '0.5rem',
                                borderBottom: '1px solid rgba(0,0,0,0.08)'
                            }}>
                                Using Advanced Filters
                            </Header>
                            <div style={{
                                padding: '1.5rem',
                                background: '#ffffff',
                                borderRadius: '8px',
                                border: '1px solid rgba(0,0,0,0.06)',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                            }}>
                                <p style={{ marginBottom: '1rem', color: '#062D4D', lineHeight: '1.7' }}>
                                    The advanced filter system with global search is available in <strong>Approval History</strong> and <strong>All Approval History</strong> views. 
                                    These views provide powerful search and filtering capabilities including global search across all fields, multi-select filters, date ranges, and saved filter presets.
                                </p>
                                <Message info style={{ marginBottom: '1.5rem', borderRadius: '8px' }}>
                                    <Message.Header style={{ fontWeight: '600' }}>Filter Availability</Message.Header>
                                    <p style={{ marginTop: '0.5rem', marginBottom: 0, lineHeight: '1.7' }}>
                                        <strong>Advanced Filters (Global Search):</strong> Available in Approval History and All Approval History views only.
                                        <br />
                                        <strong>Basic Filters:</strong> Available in My Submitted Requests and All Events views.
                                    </p>
                                </Message>
                                
                                <div style={{ marginBottom: '1.5rem' }}>
                                    <Header as="h4" style={{ 
                                        marginTop: 0,
                                        marginBottom: '0.75rem',
                                        color: '#062D4D',
                                        fontSize: '1.1rem',
                                        fontWeight: '600'
                                    }}>
                                        Step 1: Open the Filter Panel
                                    </Header>
                                    <List ordered relaxed style={{ marginLeft: '1.5rem' }}>
                                        {[
                                            'Click the "Show Filters" button (with filter icon) above the request table',
                                            'A sidebar panel will slide in from the right side of the screen',
                                            'The panel has three tabs: Search, Filters, and Saved',
                                            'You can close the panel by clicking the X button or clicking outside the panel'
                                        ].map((item, i) => (
                                            <List.Item key={i} style={{ padding: '0.4rem 0', fontSize: '0.95rem', lineHeight: '1.6' }}>
                                                {item}
                                            </List.Item>
                                        ))}
                                    </List>
                                </div>

                                <Divider style={{ margin: '1.5rem 0' }} />

                                <div style={{ marginBottom: '1.5rem' }}>
                                    <Header as="h4" style={{ 
                                        marginTop: 0,
                                        marginBottom: '0.75rem',
                                        color: '#062D4D',
                                        fontSize: '1.1rem',
                                        fontWeight: '600'
                                    }}>
                                        Step 2: Using Global Search (Search Tab)
                                    </Header>
                                    <List ordered relaxed style={{ marginLeft: '1.5rem' }}>
                                        {[
                                            'Click on the "Search" tab in the filter panel',
                                            'Type your search term in the "Global Search" field',
                                            'Global search searches across ALL fields: Request ID, Cloud Name, Category, Status, Submitted By, Dates, Comments, etc.',
                                            'As you type, the table automatically filters to show matching results',
                                            'Matching text is highlighted in yellow in the table for easy identification',
                                            'You can also use Quick Filters below for specific fields: Request ID, Cloud Name, or Submitted By'
                                        ].map((item, i) => (
                                            <List.Item key={i} style={{ padding: '0.4rem 0', fontSize: '0.95rem', lineHeight: '1.6' }}>
                                                {item}
                                            </List.Item>
                                        ))}
                                    </List>
                                </div>

                                <Divider style={{ margin: '1.5rem 0' }} />

                                <div style={{ marginBottom: '1.5rem' }}>
                                    <Header as="h4" style={{ 
                                        marginTop: 0,
                                        marginBottom: '0.75rem',
                                        color: '#062D4D',
                                        fontSize: '1.1rem',
                                        fontWeight: '600'
                                    }}>
                                        Step 3: Using Advanced Filters (Filters Tab)
                                    </Header>
                                    <List ordered relaxed style={{ marginLeft: '1.5rem' }}>
                                        {[
                                            'Click on the "Filters" tab',
                                            'Status (Multi-select): Click the dropdown to select one or more statuses (e.g., PENDING_APPROVER, APPROVED, REJECTED)',
                                            'Selected statuses appear as blue badges - click the X on a badge to remove it',
                                            'Category (Multi-select): Similarly select one or more categories (DEV, QA, STAGE, PROD)',
                                            'Date Range: Use "From" and "To" date pickers to filter requests within a specific time period',
                                            'All active filters are highlighted with blue borders and show an "Active" label',
                                            'You can combine multiple filters - they work together (AND logic)'
                                        ].map((item, i) => (
                                            <List.Item key={i} style={{ padding: '0.4rem 0', fontSize: '0.95rem', lineHeight: '1.6' }}>
                                                {item}
                                            </List.Item>
                                        ))}
                                    </List>
                                </div>

                                <Divider style={{ margin: '1.5rem 0' }} />

                                <div style={{ marginBottom: '1.5rem' }}>
                                    <Header as="h4" style={{ 
                                        marginTop: 0,
                                        marginBottom: '0.75rem',
                                        color: '#062D4D',
                                        fontSize: '1.1rem',
                                        fontWeight: '600'
                                    }}>
                                        Step 4: Saving Filter Presets (Saved Tab)
                                    </Header>
                                    <List ordered relaxed style={{ marginLeft: '1.5rem' }}>
                                        {[
                                            'After setting up your desired filters, go to the footer of the filter panel',
                                            'Enter a name for your filter combination in the "Save as..." field (e.g., "My Pending Requests")',
                                            'Click the save icon button',
                                            'Your filter preset is now saved and appears in the "Saved" tab',
                                            'To load a saved preset: Go to "Saved" tab → Click "Load" on the preset you want',
                                            'To delete a saved preset: Click the trash icon next to the preset',
                                            'Saved filters are stored locally in your browser and persist across sessions'
                                        ].map((item, i) => (
                                            <List.Item key={i} style={{ padding: '0.4rem 0', fontSize: '0.95rem', lineHeight: '1.6' }}>
                                                {item}
                                            </List.Item>
                                        ))}
                                    </List>
                                </div>

                                <Divider style={{ margin: '1.5rem 0' }} />

                                <div style={{ marginBottom: '1rem' }}>
                                    <Header as="h4" style={{ 
                                        marginTop: 0,
                                        marginBottom: '0.75rem',
                                        color: '#062D4D',
                                        fontSize: '1.1rem',
                                        fontWeight: '600'
                                    }}>
                                        Understanding Filter Indicators
                                    </Header>
                                    <List bulleted relaxed style={{ marginLeft: '1.5rem' }}>
                                        {[
                                            'Active Filter Badge: The filter button shows a blue circular badge with the number of active filters',
                                            'Blue Borders: Active filter inputs have blue borders and shadows',
                                            'Active Labels: Each active filter shows a small blue "Active" label below it',
                                            'Highlighted Results: Search terms are highlighted in yellow in table cells',
                                            'Filter Count: The footer shows total active filters (e.g., "3 active filters")'
                                        ].map((item, i) => (
                                            <List.Item key={i} style={{ padding: '0.4rem 0', fontSize: '0.95rem', lineHeight: '1.6' }}>
                                                {item}
                                            </List.Item>
                                        ))}
                                    </List>
                                </div>

                                <Message info style={{ 
                                    borderRadius: '8px',
                                    border: '1px solid rgba(53, 183, 244, 0.2)',
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                                    marginTop: '1rem'
                                }}>
                                    <Message.Header style={{ fontWeight: '600' }}>Pro Tip</Message.Header>
                                    <p style={{ marginTop: '0.5rem', marginBottom: 0, lineHeight: '1.7' }}>
                                        Combine Global Search with specific filters for more precise results. For example, search for "testdb" globally 
                                        while filtering by the status "APPROVED" to find all approved requests containing "testdb".
                                    </p>
                                </Message>
                            </div>
                        </div>

                        <div style={{ marginBottom: '2rem' }}>
                            <Header as="h3" style={{ 
                                marginBottom: '1rem',
                                color: '#062D4D',
                                fontSize: '1.4rem',
                                fontWeight: '600',
                                paddingBottom: '0.5rem',
                                borderBottom: '1px solid rgba(0,0,0,0.08)'
                            }}>
                                Understanding Real-Time Notifications
                            </Header>
                            <div style={{
                                padding: '1.5rem',
                                background: '#ffffff',
                                borderRadius: '8px',
                                border: '1px solid rgba(0,0,0,0.06)',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                            }}>
                                <p style={{ marginBottom: '1rem', color: '#062D4D', lineHeight: '1.7' }}>
                                    Real-time notifications keep you informed of important events without the need to refresh the page manually.
                                </p>
                                
                                <div style={{ marginBottom: '1.5rem' }}>
                                    <Header as="h4" style={{ 
                                        marginTop: 0,
                                        marginBottom: '0.75rem',
                                        color: '#062D4D',
                                        fontSize: '1.1rem',
                                        fontWeight: '600'
                                    }}>
                                        Step 1: Enable Browser Notifications
                                    </Header>
                                    <List ordered relaxed style={{ marginLeft: '1.5rem' }}>
                                        {[
                                            'Click the bell icon (🔔) in the top-right corner of the navbar',
                                            'If notifications are not enabled, you\'ll see an "Enable Notifications" button',
                                            'Click "Enable Notifications"',
                                            'Your browser will ask for permission - click "Allow"',
                                            'Once enabled, you\'ll receive browser notifications even when the tab is not active'
                                        ].map((item, i) => (
                                            <List.Item key={i} style={{ padding: '0.4rem 0', fontSize: '0.95rem', lineHeight: '1.6' }}>
                                                {item}
                                            </List.Item>
                                        ))}
                                    </List>
                                </div>

                                <Divider style={{ margin: '1.5rem 0' }} />

                                <div style={{ marginBottom: '1.5rem' }}>
                                    <Header as="h4" style={{ 
                                        marginTop: 0,
                                        marginBottom: '0.75rem',
                                        color: '#062D4D',
                                        fontSize: '1.1rem',
                                        fontWeight: '600'
                                    }}>
                                        Step 2: Using the Notification Center
                                    </Header>
                                    <List ordered relaxed style={{ marginLeft: '1.5rem' }}>
                                        {[
                                            'Click the bell icon to open the notification center',
                                            'The badge on the bell shows the number of unread notifications',
                                            'Notifications are grouped by type: New Requests, Status Changes, VM Updates, etc.',
                                            'Click on a notification to view the related request',
                                            'Notifications are marked as read when you click on them',
                                            'Use "Mark All as Read" to clear all unread notifications',
                                            'Notifications are automatically updated in real-time as events occur'
                                        ].map((item, i) => (
                                            <List.Item key={i} style={{ padding: '0.4rem 0', fontSize: '0.95rem', lineHeight: '1.6' }}>
                                                {item}
                                            </List.Item>
                                        ))}
                                    </List>
                                </div>

                                <Divider style={{ margin: '1.5rem 0' }} />

                                <div style={{ marginBottom: '1.5rem' }}>
                                    <Header as="h4" style={{ 
                                        marginTop: 0,
                                        marginBottom: '0.75rem',
                                        color: '#062D4D',
                                        fontSize: '1.1rem',
                                        fontWeight: '600'
                                    }}>
                                        Types of Notifications You'll Receive
                                    </Header>
                                    <List bulleted relaxed style={{ marginLeft: '1.5rem' }}>
                                        {[
                                            { type: 'New Request', desc: 'When a new request needs your approval (for Approvers)' },
                                            { type: 'Status Change', desc: 'When a request you\'re involved with changes status' },
                                            { type: 'VM Provisioning', desc: 'Updates on VM creation progress and completion' },
                                            { type: 'Approval Needed', desc: 'Reminders when requests are pending your action' },
                                            { type: 'Request Rejected', desc: 'When your request is rejected' },
                                            { type: 'Request Approved', desc: 'When your request is approved' }
                                        ].map((item, i) => (
                                            <List.Item key={i} style={{ padding: '0.4rem 0', fontSize: '0.95rem', lineHeight: '1.6' }}>
                                                <strong style={{ color: '#062D4D' }}>{item.type}:</strong> {item.desc}
                                            </List.Item>
                                        ))}
                                    </List>
                                </div>

                                <Message warning style={{ 
                                    borderRadius: '8px',
                                    border: '1px solid rgba(242, 113, 28, 0.2)',
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                                    marginTop: '1rem'
                                }}>
                                    <Message.Header style={{ fontWeight: '600' }}>Important</Message.Header>
                                    <p style={{ marginTop: '0.5rem', marginBottom: 0, lineHeight: '1.7' }}>
                                        Browser notifications require notification permissions to be enabled in your browser. If you previously blocked notifications, 
                                        you can re-enable them through your browser settings. In-app notifications will continue to work regardless 
                                        of your browser notification settings.
                                    </p>
                                </Message>
                            </div>
                        </div>

                        <Message warning style={{ 
                            borderRadius: '8px',
                            border: '1px solid rgba(242, 113, 28, 0.2)',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                        }}>
                            <Message.Header style={{ fontWeight: '600' }}>Important Notes</Message.Header>
                            <List bulleted relaxed style={{ marginTop: '0.5rem', marginLeft: '1.5rem' }}>
                                {[
                                    'Ensure all required fields are completed before submitting your request',
                                    'Cloud names must be unique across the entire system',
                                    'You will receive email notifications at each stage of the approval process',
                                    'Contact your approver if you need to modify a request that has already been submitted'
                                ].map((item, i) => (
                                    <List.Item key={i} style={{ padding: '0.3rem 0', fontSize: '0.95rem', lineHeight: '1.6' }}>
                                        {item}
                                    </List.Item>
                                ))}
                            </List>
                        </Message>
                    </div>
                );

            case 'approver':
                return (
                    <div>
                        <div style={{ 
                            marginBottom: '2rem',
                            paddingBottom: '1.5rem',
                            borderBottom: '2px solid rgba(242, 113, 28, 0.15)'
                        }}>
                            <Header as="h2" style={{ 
                                margin: 0, 
                                color: '#f2711c',
                                fontSize: '2rem',
                                fontWeight: '600',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                                lineHeight: '1.2'
                            }}>
                                <Icon name="check circle" style={{ flexShrink: 0, verticalAlign: 'middle' }} />
                                <span>User Guide (Approver)</span>
                            </Header>
                        </div>

                        {[
                            {
                                title: 'Approver Responsibilities',
                                content: 'As an Approver, you are responsible for reviewing and acting on VM provisioning requests. Incoming requests are routed directly to you for review and approval.'
                            },
                            {
                                title: 'Reviewing Requests',
                                content: 'Access pending requests through the "Approver View" tab:',
                                list: [
                                    'View all requests that are awaiting your approval',
                                    'Use the filter panel to search and narrow results by status, cloud name, category, or date range',
                                    'Click on a request to view its full details',
                                    'Review the request information, submitter details, and approval history timeline',
                                    'Search terms are highlighted in yellow for quick identification'
                                ]
                            },
                            {
                                title: 'Using Advanced Filters',
                                content: 'Advanced filters with global search are available in the "Approval History" and "All Approval History" tabs. The filter panel provides powerful search capabilities:',
                                list: [
                                    'Click "Show Filters" to open the sidebar filter panel',
                                    'Use Global Search to search across all fields simultaneously',
                                    'Apply multiple filters: status, category, date ranges, submitted by',
                                    'Save frequently used filter combinations for quick access',
                                    'Active filters are highlighted with blue borders and badges',
                                    'Search terms are highlighted in table results'
                                ]
                            },
                            {
                                title: 'Approving Requests',
                                content: 'When approving a request:',
                                list: [
                                    'Click Approve on the request details',
                                    'For Azure requests: Select the Azure subscription and tenant',
                                    'For Linode requests: Select the Linode account (POC, DEVQA, or CUST)',
                                    'For Multi Tenancy Form requests: Subscription/account selection may be automatic based on category',
                                    'System will initiate VM/Instance creation on the selected cloud platform',
                                    'You can view live provisioning logs and cancel operations if needed',
                                    'Submitter and relevant parties receive email notifications'
                                ]
                            },
                            {
                                title: 'Rejecting Requests',
                                content: 'If a request does not meet the requirements:',
                                list: [
                                    'Click Reject on the request',
                                    'Provide a reason for rejection (optional but recommended for clarity)',
                                    'The submitter is notified of the rejection along with any comments',
                                    'Request status changes to REJECTED'
                                ]
                            },
                            {
                                title: 'Approval History',
                                content: 'Use the "Approval History" tab to review all requests you have previously approved or rejected:',
                                list: [
                                    'View a complete record of your past approvals and rejections',
                                    'Use advanced filters with global search to locate specific requests quickly',
                                    'Search terms are highlighted in results for easy scanning',
                                    'Filter by date range to review requests from a specific time period',
                                    'Save filter combinations as presets for repeated use'
                                ]
                            },
                            {
                                title: 'Real-Time Notifications',
                                content: 'Stay updated with instant notifications:',
                                list: [
                                    'Receive browser notifications when new requests need your approval',
                                    'Get notified when request status changes',
                                    'View all notifications in the notification center (bell icon)',
                                    'Notifications are organized by type and show unread counts'
                                ]
                            }
                        ].map((section, idx) => (
                            <div key={idx} style={{ 
                                marginBottom: '1.5rem',
                                padding: '1.5rem',
                                background: '#ffffff',
                                borderRadius: '8px',
                                border: '1px solid rgba(0,0,0,0.06)',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                            }}>
                                <Header as="h3" style={{ 
                                    marginTop: 0,
                                    marginBottom: '1rem',
                                    color: '#062D4D',
                                    fontSize: '1.25rem',
                                    fontWeight: '600'
                                }}>
                                    {section.title}
                                </Header>
                                <p style={{ marginBottom: section.list ? '1rem' : 0, color: '#062D4D', lineHeight: '1.7' }}>
                                    {section.content}
                                </p>
                                {section.list && (
                                    <List ordered relaxed style={{ marginTop: '0.5rem', marginLeft: '1.5rem' }}>
                                        {section.list.map((item, i) => (
                                            <List.Item key={i} style={{ padding: '0.4rem 0', fontSize: '0.95rem', lineHeight: '1.6' }}>
                                                {item}
                                            </List.Item>
                                        ))}
                                    </List>
                                )}
                            </div>
                        ))}

                        <Message info style={{ 
                            borderRadius: '8px',
                            border: '1px solid rgba(53, 183, 244, 0.2)',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                        }}>
                            <Message.Header style={{ fontWeight: '600' }}>Best Practices</Message.Header>
                            <List bulleted relaxed style={{ marginTop: '0.5rem', marginLeft: '1.5rem' }}>
                                {[
                                    'Review requests promptly to minimize delays in provisioning',
                                    'Verify that the resource requirements are appropriate for the request',
                                    'Ensure the correct Azure subscription or Linode account is selected before approving',
                                    'Monitor live provisioning logs and be prepared to cancel if any issues arise',
                                    'For migration requests: note that these require DevOps approval rather than standard approver approval'
                                ].map((item, i) => (
                                    <List.Item key={i} style={{ padding: '0.3rem 0', fontSize: '0.95rem', lineHeight: '1.6' }}>
                                        {item}
                                    </List.Item>
                                ))}
                            </List>
                        </Message>
                    </div>
                );

            case 'workflow':
                return (
                    <div>
                        <div style={{ 
                            marginBottom: '2rem',
                            paddingBottom: '1.5rem',
                            borderBottom: '2px solid rgba(100, 53, 201, 0.15)'
                        }}>
                            <Header as="h2" style={{ 
                                margin: 0, 
                                color: '#6435c9',
                                fontSize: '2rem',
                                fontWeight: '600',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                                lineHeight: '1.2'
                            }}>
                                <Icon name="sitemap" style={{ flexShrink: 0, verticalAlign: 'middle' }} />
                                <span>Request Workflow</span>
                            </Header>
                        </div>

                        {/* Workflow Types Overview */}
                        <Message info style={{ 
                            borderRadius: '8px',
                            border: '1px solid rgba(53, 183, 244, 0.2)',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                            marginBottom: '2rem',
                            padding: '1.25rem'
                        }}>
                            <Message.Header style={{ fontWeight: '600', fontSize: '1rem', marginBottom: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <Icon name="info circle" />
                                Two Request Workflows
                            </Message.Header>
                            <p style={{ marginTop: '0.5rem', marginBottom: '0.75rem', lineHeight: '1.7', fontSize: '0.95rem' }}>
                                tenantBridge supports <strong>two request workflows</strong>, each suited to different use cases:
                            </p>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1rem', marginTop: '0.75rem' }}>
                                <div style={{
                                    padding: '1rem',
                                    background: '#ffffff',
                                    borderRadius: '6px',
                                    border: '2px solid #062D4D',
                                    boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                                        <Icon name="sitemap" style={{ color: '#062D4D', fontSize: '1.2em' }} />
                                        <strong style={{ color: '#062D4D', fontSize: '0.95rem' }}>Standard Workflow</strong>
                                    </div>
                                    <p style={{ margin: 0, fontSize: '0.9rem', color: '#666', lineHeight: '1.6' }}>
                                        User → Approver → VM Provisioning
                                    </p>
                                </div>
                                <div style={{
                                    padding: '1rem',
                                    background: '#ffffff',
                                    borderRadius: '6px',
                                    border: '2px solid #6F9911',
                                    boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                                        <Icon name="bolt" style={{ color: '#6F9911', fontSize: '1.2em' }} />
                                        <strong style={{ color: '#6F9911', fontSize: '0.95rem' }}>Auto-Provisioning</strong>
                                    </div>
                                    <p style={{ margin: 0, fontSize: '0.9rem', color: '#666', lineHeight: '1.6' }}>
                                        User (Self-Approval) → VM Provisioning
                                    </p>
                                </div>
                            </div>
                        </Message>

                        {/* Visual Workflow Diagrams */}
                        <div style={{ marginBottom: '2rem' }}>
                            <Header as="h3" style={{ 
                                marginBottom: '1.5rem',
                                color: '#062D4D',
                                fontSize: '1.4rem',
                                fontWeight: '600',
                                paddingBottom: '0.5rem',
                                borderBottom: '1px solid rgba(0,0,0,0.08)',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem'
                            }}>
                                <Icon name="sitemap" />
                                Visual Workflow Diagrams
                            </Header>

                            {/* Standard Workflow Diagram */}
                            <div style={{
                                padding: '2rem',
                                background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
                                borderRadius: '8px',
                                border: '2px solid #062D4D',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                marginBottom: '2rem'
                            }}>
                                <div style={{ 
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem',
                                    marginBottom: '1.5rem',
                                    paddingBottom: '0.75rem',
                                    borderBottom: '2px solid #062D4D'
                                }}>
                                    <Icon name="sitemap" style={{ color: '#062D4D', fontSize: '1.2em' }} />
                                    <Header as="h4" style={{ margin: 0, color: '#062D4D', fontSize: '1.1rem', fontWeight: '600' }}>
                                        Standard Approval Workflow
                                    </Header>
                                </div>
                                <div style={{ 
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    gap: '0.75rem',
                                    fontSize: '0.95rem'
                                }}>
                                    <div style={{ 
                                        padding: '0.75rem 1.5rem',
                                        background: '#062D4D',
                                        color: '#ffffff',
                                        borderRadius: '6px',
                                        fontWeight: '600',
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.5rem'
                                    }}>
                                        <Icon name="user" />
                                        User → Submit Request
                                    </div>
                                    <Icon name="arrow down" style={{ color: '#666', fontSize: '1.2em' }} />
                                    <div style={{ 
                                        padding: '0.75rem 1.5rem',
                                        background: '#f2711c',
                                        color: '#ffffff',
                                        borderRadius: '6px',
                                        fontWeight: '600',
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                    }}>
                                        PENDING_APPROVER
                                    </div>
                                    <Icon name="arrow down" style={{ color: '#666', fontSize: '1.2em' }} />
                                    <div style={{ 
                                        padding: '0.75rem 1.5rem',
                                        background: '#062D4D',
                                        color: '#ffffff',
                                        borderRadius: '6px',
                                        fontWeight: '600',
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.5rem'
                                    }}>
                                        <Icon name="check circle" />
                                        Approver → Review & Approve
                                    </div>
                                    <Icon name="arrow down" style={{ color: '#666', fontSize: '1.2em' }} />
                                    <div style={{ 
                                        padding: '0.75rem 1.5rem',
                                        background: '#6F9911',
                                        color: '#ffffff',
                                        borderRadius: '6px',
                                        fontWeight: '600',
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.5rem'
                                    }}>
                                        <Icon name="server" />
                                        APPROVED → VM Provisioning
                                    </div>
                                    <Icon name="arrow down" style={{ color: '#666', fontSize: '1.2em' }} />
                                    <div style={{ 
                                        padding: '0.75rem 1.5rem',
                                        background: '#6F9911',
                                        color: '#ffffff',
                                        borderRadius: '6px',
                                        fontWeight: '600',
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.5rem'
                                    }}>
                                        <Icon name="checkmark circle" />
                                        COMPLETED ✓
                                    </div>
                                </div>
                            </div>

                            {/* Auto-Provisioning Workflow Diagram */}
                            <div style={{
                                padding: '2rem',
                                background: 'linear-gradient(135deg, #f0fdf4 0%, #ffffff 100%)',
                                borderRadius: '8px',
                                border: '2px solid #6F9911',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                            }}>
                                <div style={{ 
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem',
                                    marginBottom: '1.5rem',
                                    paddingBottom: '0.75rem',
                                    borderBottom: '2px solid #6F9911'
                                }}>
                                    <Icon name="bolt" style={{ color: '#6F9911', fontSize: '1.2em' }} />
                                    <Header as="h4" style={{ margin: 0, color: '#6F9911', fontSize: '1.1rem', fontWeight: '600' }}>
                                        Auto-Provisioning Workflow
                                    </Header>
                                    <Label color="green" size="small" style={{ marginLeft: 'auto', fontWeight: '600' }}>
                                        Faster Path
                                    </Label>
                                </div>
                                <div style={{ 
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    gap: '0.75rem',
                                    fontSize: '0.95rem'
                                }}>
                                    <div style={{ 
                                        padding: '0.75rem 1.5rem',
                                        background: '#6F9911',
                                        color: '#ffffff',
                                        borderRadius: '6px',
                                        fontWeight: '600',
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.5rem'
                                    }}>
                                        <Icon name="user" />
                                        User → Submit Request (with Auto-Provisioning)
                                    </div>
                                    <Icon name="arrow down" style={{ color: '#666', fontSize: '1.2em' }} />
                                    <div style={{ 
                                        padding: '0.75rem 1.5rem',
                                        background: '#f2711c',
                                        color: '#ffffff',
                                        borderRadius: '6px',
                                        fontWeight: '600',
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                    }}>
                                        PENDING_APPROVER
                                        <Label color="green" size="tiny" style={{ marginLeft: '0.5rem', fontWeight: '600' }}>
                                            auto
                                        </Label>
                                    </div>
                                    <Icon name="arrow down" style={{ color: '#666', fontSize: '1.2em' }} />
                                    <div style={{ 
                                        padding: '0.75rem 1.5rem',
                                        background: '#6F9911',
                                        color: '#ffffff',
                                        borderRadius: '6px',
                                        fontWeight: '600',
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.5rem'
                                    }}>
                                        <Icon name="user check" />
                                        Self-Approval (Same User)
                                    </div>
                                    <Icon name="arrow down" style={{ color: '#666', fontSize: '1.2em' }} />
                                    <div style={{ 
                                        padding: '0.75rem 1.5rem',
                                        background: '#6F9911',
                                        color: '#ffffff',
                                        borderRadius: '6px',
                                        fontWeight: '600',
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.5rem'
                                    }}>
                                        <Icon name="server" />
                                        APPROVED → VM Provisioning
                                    </div>
                                    <Icon name="arrow down" style={{ color: '#666', fontSize: '1.2em' }} />
                                    <div style={{ 
                                        padding: '0.75rem 1.5rem',
                                        background: '#6F9911',
                                        color: '#ffffff',
                                        borderRadius: '6px',
                                        fontWeight: '600',
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.5rem'
                                    }}>
                                        <Icon name="checkmark circle" />
                                        COMPLETED ✓
                                    </div>
                                </div>
                                <div style={{
                                    marginTop: '1.5rem',
                                    padding: '1rem',
                                    background: '#f0fdf4',
                                    borderRadius: '6px',
                                    border: '1px solid #6F9911',
                                    fontSize: '0.9rem',
                                    color: '#166534',
                                    textAlign: 'center'
                                }}>
                                    <Icon name="info circle" style={{ marginRight: '0.5rem' }} />
                                    <strong>Note:</strong> Auto-provisioning enables self-approval, allowing authorized users to expedite the process.
                                </div>
                            </div>
                        </div>

                        {/* Standard Workflow */}
                        <div style={{ marginBottom: '2.5rem' }}>
                            <Header as="h3" style={{ 
                                marginBottom: '1.5rem',
                                color: '#062D4D',
                                fontSize: '1.4rem',
                                fontWeight: '600',
                                paddingBottom: '0.5rem',
                                borderBottom: '2px solid #062D4D',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem'
                            }}>
                                <Icon name="sitemap" style={{ color: '#062D4D' }} />
                                Standard Approval Workflow
                            </Header>
                            
                            {[
                                {
                                    step: 'Step 1: Request Submission',
                                    icon: 'edit',
                                    color: '#062D4D',
                                    items: [
                                        'User submits a VM request via the Single Tenancy Form or Multi Tenancy Form',
                                        'User selects the cloud provider: Azure or Linode (based on form type and category)',
                                        'The system validates all required fields (category, cloud provider, VM size, location, etc.)',
                                        'For migration requests: the system requires DevOps approval, bypassing regular approvers',
                                        'A unique Request ID is automatically generated (e.g., REQ-1234567890-abc123)',
                                        'Request status is set to: PENDING_APPROVER',
                                        'An email notification is sent to the assigned approvers (or DevOps for migration requests)',
                                        'A real-time notification appears in the approvers\' notification center'
                                    ]
                                },
                                {
                                    step: 'Step 2: Approver Review',
                                    icon: 'check circle',
                                    color: '#f2711c',
                                    items: [
                                        'The approver receives an email and a real-time browser notification',
                                        'The approver opens the "Approver View" tab to see pending requests',
                                        'The approver clicks "View Details" to review the complete request information',
                                        'For Azure requests: the approver selects the Azure subscription and tenant',
                                        'For Linode requests: the approver selects the Linode account (POC, DEVQA, or CUST)',
                                        'For Multi Tenancy Form requests: subscription/account selection may be handled automatically based on category',
                                        'The approver can view live provisioning logs and cancel operations if needed',
                                        'The approver can either:',
                                        '  • Approve → Status changes to APPROVED and VM/Instance provisioning begins',
                                        '  • Reject → Status changes to REJECTED (workflow ends)',
                                        'An email notification is sent to the submitter'
                                    ]
                                },
                                {
                                    step: 'Step 3: VM Provisioning',
                                    icon: 'server',
                                    color: '#6F9911',
                                    items: [
                                        'The system automatically initiates VM/Instance creation on the selected cloud platform',
                                        'For Azure: a VM is created in the selected subscription with all configured resources (resource group, storage account, network, NSG, NIC, public IP)',
                                        'For Linode: an instance is created with the specified configuration (region, type/plan, image, root password, SSH keys)',
                                        'The system configures storage (Azure Blob, CPG DataCenter, BYOS S3-Compatible, or Linode storage), networking, and security',
                                        'Post-provisioning setup includes SSH configuration, system setup, software installation, and network configuration',
                                        'For Multi Tenancy requests: server configuration is updated (Nginx, DNS resolution, multi-tenancy setup)',
                                        'Status transitions from PROVISIONING → COMPLETED (or TENANT IS READY for Multi Tenancy)',
                                        'Real-time progress updates are sent via notifications',
                                        'The approver can view live provisioning logs in the approval modal and cancel the operation if needed'
                                    ]
                                },
                                {
                                    step: 'Step 4: Completion',
                                    icon: 'checkmark circle',
                                    color: '#6F9911',
                                    items: [
                                        'VM/Instance provisioning completes successfully',
                                        'Status changes to COMPLETED (or TENANT IS READY for Multi Tenancy, MULTI TENANCY FORM COMPLETED for finished multi-tenancy workflows)',
                                        'VM/Instance credentials and access information are automatically sent via email',
                                        'The portal URL is included in the completion email',
                                        'Credentials are stored securely in Keeper Security Manager',
                                        'For PROD or CUST category (Single-Tenancy): Blue "Request Credentials via JIRA" button appears in the Actions column — opens a pre-filled JIRA ticket for the DevOps team',
                                        'For all other categories (STAGE, ENGG_DEV, ENGG_QA, DEMO, TESTING, POC, etc.): Green "Access Credentials via Keeper" button appears — opens the Keeper Security vault',
                                        'For PROD category (Multi-Tenancy, non-POC): "Request Credentials via JIRA" button appears',
                                        'For PROD category (Multi-Tenancy, POC) or CUST (Multi-Tenancy): "Access Credentials via Keeper" button appears',
                                        'Request appears in all history views as completed',
                                        'Submitter can access VM/Instance details and credentials through request history',
                                        'Account deletion by user: Sends confirmation email to user and notification email to all DevOps team members'
                                    ]
                                },
                                {
                                    step: 'Error Handling',
                                    icon: 'warning circle',
                                    color: '#CE1B4E',
                                    items: [
                                        'If provisioning fails at any stage, the status changes to ERROR or a specific failure status (e.g., FAILED-VM Creation, FAILED-Setup, FAILED-Multi Tenancy Form Script)',
                                        'Detailed error information is logged and visible in the request details',
                                        'An email notification is sent to both the submitter and approver with error details',
                                        'The request can be reviewed, corrected, and resubmitted as needed',
                                        'Error logs are available in the "View Details" modal for troubleshooting',
                                        'Approvers can cancel ongoing provisioning operations if needed (resulting status: VM_CREATION_CANCELLED or LINODE_INSTANCE_CREATION_CANCELLED)'
                                    ]
                                }
                            ].map((section, idx) => (
                                <div key={idx} style={{ 
                                    marginBottom: '1.75rem',
                                    padding: '1.75rem',
                                    background: '#ffffff',
                                    borderRadius: '8px',
                                    border: `2px solid ${section.color}30`,
                                    borderLeft: `5px solid ${section.color}`,
                                    boxShadow: '0 2px 6px rgba(0,0,0,0.08)'
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
                                        <div style={{
                                            width: '40px',
                                            height: '40px',
                                            borderRadius: '50%',
                                            background: `linear-gradient(135deg, ${section.color} 0%, ${section.color}dd 100%)`,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            color: '#ffffff',
                                            fontWeight: '700',
                                            fontSize: '1.1rem',
                                            flexShrink: 0,
                                            boxShadow: `0 2px 8px ${section.color}40`
                                        }}>
                                            {idx + 1}
                                        </div>
                                        <div style={{ flex: 1 }}>
                                    <Header as="h4" style={{ 
                                                margin: 0,
                                                color: section.color,
                                                fontSize: '1.2rem',
                                                fontWeight: '600',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '0.5rem'
                                            }}>
                                                <Icon name={section.icon} />
                                                {section.step}
                                            </Header>
                                        </div>
                                    </div>
                                    <List bulleted relaxed style={{ marginLeft: '1.5rem', marginTop: '0.5rem' }}>
                                        {section.items.map((item, i) => (
                                            <List.Item key={i} style={{ padding: '0.5rem 0', fontSize: '0.95rem', lineHeight: '1.7', color: '#062D4D' }}>
                                                {item}
                                            </List.Item>
                                        ))}
                                    </List>
                                </div>
                            ))}
                        </div>

                        {/* Auto-Provisioning Workflow */}
                        <div style={{ marginBottom: '2.5rem' }}>
                            <Header as="h3" style={{ 
                                marginBottom: '1.5rem',
                                color: '#062D4D',
                                fontSize: '1.4rem',
                                fontWeight: '600',
                                paddingBottom: '0.5rem',
                                borderBottom: '2px solid #6F9911',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem'
                            }}>
                                <Icon name="bolt" style={{ color: '#6F9911' }} />
                                Auto-Provisioning Workflow
                            </Header>
                            
                            <Message warning style={{ 
                                borderRadius: '8px',
                                border: '1px solid rgba(242, 113, 28, 0.2)',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                                marginBottom: '1.5rem'
                            }}>
                                <Message.Header style={{ fontWeight: '600', fontSize: '0.95rem' }}>
                                    <Icon name="info circle" />
                                    Auto-Provisioning Requirements
                                </Message.Header>
                                <p style={{ marginTop: '0.5rem', marginBottom: 0, fontSize: '0.9rem', lineHeight: '1.6' }}>
                                    Auto-provisioning must be enabled for your user account and configured for specific categories.
                                    Once enabled, you can submit requests with the "auto" option and approve them yourself, bypassing the standard approval workflow.
                                </p>
                            </Message>

                            {[
                                {
                                    step: 'Step 1: Submit Auto-Provisioning Request',
                                    icon: 'edit',
                                    color: '#6F9911',
                                    items: [
                                        'A user with auto-provisioning access submits a VM request',
                                        'Enable the "Auto Provisioning" option in the form (available only for eligible categories)',
                                        'The system validates that auto-provisioning is enabled for the selected category',
                                        'A Request ID is generated and the status is set to: PENDING_APPROVER',
                                        'The request enters the approver workflow directly',
                                        'A visual "auto" badge is displayed on the request across all views'
                                    ]
                                },
                                {
                                    step: 'Step 2: Self-Approval',
                                    icon: 'user check',
                                    color: '#6F9911',
                                    items: [
                                        'The submitter receives a notification that their request is ready for approval',
                                        'The submitter opens the "Approver View" tab (acting as their own approver)',
                                        'The submitter reviews their request and clicks "Approve"',
                                        'For Azure requests: Submitter selects the Azure subscription and tenant',
                                        'For Linode requests: Submitter selects the Linode account (POC, DEVQA, or CUST)',
                                        'For Multi Tenancy Form: Subscription/account selection may be automatic',
                                        'Status changes to: APPROVED',
                                        'VM/Instance provisioning starts immediately'
                                    ]
                                },
                                {
                                    step: 'Step 3: VM Provisioning & Completion',
                                    icon: 'server',
                                    color: '#6F9911',
                                    items: [
                                        'The VM/Instance provisioning process follows the same steps as the standard workflow',
                                        'The system creates the VM/Instance on the selected cloud platform (Azure or Linode)',
                                        'Status transitions from PROVISIONING → COMPLETED (or TENANT IS READY for Multi Tenancy)',
                                        'Credentials and access information are sent via email',
                                        'Credential access buttons appear based on the category and form type (JIRA for PROD/CUST in ST, Keeper for others)',
                                        'The request appears in history with an "auto" badge indicator'
                                    ]
                                }
                            ].map((section, idx) => (
                                <div key={idx} style={{ 
                                    marginBottom: '1.75rem',
                                    padding: '1.75rem',
                                    background: '#ffffff',
                                    borderRadius: '8px',
                                    border: `2px solid ${section.color}30`,
                                    borderLeft: `5px solid ${section.color}`,
                                    boxShadow: '0 2px 6px rgba(0,0,0,0.08)'
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
                                        <div style={{
                                            width: '40px',
                                            height: '40px',
                                            borderRadius: '50%',
                                            background: `linear-gradient(135deg, ${section.color} 0%, ${section.color}dd 100%)`,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            color: '#ffffff',
                                            fontWeight: '700',
                                        fontSize: '1.1rem',
                                            flexShrink: 0,
                                            boxShadow: `0 2px 8px ${section.color}40`
                                        }}>
                                            {idx + 1}
                                        </div>
                                        <div style={{ flex: 1 }}>
                                            <Header as="h4" style={{ 
                                                margin: 0,
                                                color: section.color,
                                                fontSize: '1.2rem',
                                                fontWeight: '600',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '0.5rem'
                                            }}>
                                                <Icon name={section.icon} />
                                        {section.step}
                                    </Header>
                                        </div>
                                    </div>
                                    <List bulleted relaxed style={{ marginLeft: '1.5rem', marginTop: '0.5rem' }}>
                                        {section.items.map((item, i) => (
                                            <List.Item key={i} style={{ padding: '0.5rem 0', fontSize: '0.95rem', lineHeight: '1.7', color: '#062D4D' }}>
                                                {item}
                                            </List.Item>
                                        ))}
                                    </List>
                                </div>
                            ))}
                        </div>

                        {/* Migration Workflow */}
                        <div style={{ marginBottom: '2.5rem' }}>
                            <Header as="h3" style={{ 
                                marginBottom: '1.5rem',
                                color: '#062D4D',
                                fontSize: '1.4rem',
                                fontWeight: '600',
                                paddingBottom: '0.5rem',
                                borderBottom: '2px solid #6435c9',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem'
                            }}>
                                <Icon name="exchange" style={{ color: '#6435c9' }} />
                                Migration Workflow (Single Tenant to Multi Tenant)
                            </Header>
                            
                            <Message warning style={{ 
                                borderRadius: '8px',
                                border: '1px solid rgba(242, 113, 28, 0.2)',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                                marginBottom: '1.5rem'
                            }}>
                                <Message.Header style={{ fontWeight: '600', fontSize: '0.95rem' }}>
                                    <Icon name="info circle" />
                                    Migration Request Requirements
                                </Message.Header>
                                <p style={{ marginTop: '0.5rem', marginBottom: 0, fontSize: '0.9rem', lineHeight: '1.6' }}>
                                    Migration requests require <strong>DevOps approval</strong> and bypass the standard approver workflow. 
                                    Auto-provisioning is not available for migration requests, even if the user has it enabled for the relevant category.
                                </p>
                            </Message>

                            {[
                                {
                                    step: 'Step 1: Submit Migration Request',
                                    icon: 'exchange',
                                    color: '#6435c9',
                                    items: [
                                        'The user enables the migration toggle in the form',
                                        'For the Single Tenancy Form: enable "Is this setup being created for migrating single tenancy to v2.6.x?"',
                                        'For the Multi Tenancy Form: enable "Single Tenant to Multi Tenant Migration?"',
                                        'Enter the old tenant URL (the cloudName field is cleared for migration requests)',
                                        'The system validates the migration request and assigns DevOps as the approver',
                                        'Request status is set to: PENDING_APPROVER',
                                        'An email notification is sent to DevOps (not to regular approvers)'
                                    ]
                                },
                                {
                                    step: 'Step 2: DevOps Approval',
                                    icon: 'user shield',
                                    color: '#6435c9',
                                    items: [
                                        'DevOps receives an email and a real-time notification',
                                        'DevOps reviews the migration request details',
                                        'DevOps approves the migration request',
                                        'The status changes to: APPROVED',
                                        'VM/Instance provisioning begins with the migration configuration applied'
                                    ]
                                },
                                {
                                    step: 'Step 3: Migration Process',
                                    icon: 'sync',
                                    color: '#6435c9',
                                    items: [
                                        'The system creates a new multi-tenant VM/Instance',
                                        'Status changes to: MIGRATION IS IN PROGRESS',
                                        'Migration scripts run to transfer data and configuration',
                                        'The system configures the multi-tenancy setup',
                                        'Status changes to: TENANT IS READY or MULTI TENANCY FORM COMPLETED',
                                        'An email notification is sent with migration completion details'
                                    ]
                                }
                            ].map((section, idx) => (
                                <div key={idx} style={{ 
                                    marginBottom: '1.75rem',
                                    padding: '1.75rem',
                                    background: '#ffffff',
                                    borderRadius: '8px',
                                    border: `2px solid ${section.color}30`,
                                    borderLeft: `5px solid ${section.color}`,
                                    boxShadow: '0 2px 6px rgba(0,0,0,0.08)'
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
                                        <div style={{
                                            width: '40px',
                                            height: '40px',
                                            borderRadius: '50%',
                                            background: `linear-gradient(135deg, ${section.color} 0%, ${section.color}dd 100%)`,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            color: '#ffffff',
                                            fontWeight: '700',
                                            fontSize: '1.1rem',
                                            flexShrink: 0,
                                            boxShadow: `0 2px 8px ${section.color}40`
                                        }}>
                                            {idx + 1}
                                        </div>
                                        <div style={{ flex: 1 }}>
                                            <Header as="h4" style={{ 
                                                margin: 0,
                                                color: section.color,
                                                fontSize: '1.2rem',
                                                fontWeight: '600',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '0.5rem'
                                            }}>
                                                <Icon name={section.icon} />
                                                {section.step}
                                            </Header>
                                        </div>
                                    </div>
                                    <List bulleted relaxed style={{ marginLeft: '1.5rem', marginTop: '0.5rem' }}>
                                        {section.items.map((item, i) => (
                                            <List.Item key={i} style={{ padding: '0.5rem 0', fontSize: '0.95rem', lineHeight: '1.7', color: '#062D4D' }}>
                                                {item}
                                            </List.Item>
                                        ))}
                                    </List>
                                </div>
                            ))}
                        </div>

                        {/* Status Reference */}
                        <div style={{
                            padding: '1.75rem',
                            background: '#ffffff',
                            borderRadius: '8px',
                            border: '1px solid rgba(0,0,0,0.06)',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                            marginBottom: '1.5rem'
                        }}>
                            <Header as="h3" style={{ 
                                marginTop: 0,
                                marginBottom: '1.25rem',
                                color: '#062D4D',
                                fontSize: '1.25rem',
                                fontWeight: '600',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem'
                            }}>
                                <Icon name="list" />
                                Request Status Reference
                            </Header>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem' }}>
                                {[
                                    { status: 'PENDING_APPROVER', desc: 'Waiting for approver approval', color: '#f2711c' },
                                    { status: 'APPROVED', desc: 'Approved and VM provisioning initiated', color: '#6F9911' },
                                    { status: 'PROVISIONING', desc: 'VM/Instance is being created on cloud platform (Azure or Linode)', color: '#062D4D' },
                                    { status: 'COMPLETED', desc: 'VM/Instance successfully provisioned and configured', color: '#6F9911' },
                                    { status: 'TENANT IS READY', desc: 'Multi-tenancy setup completed and tenant is ready', color: '#6F9911' },
                                    { status: 'MIGRATION IS IN PROGRESS', desc: 'Single tenant to multi-tenant migration in progress', color: '#062D4D' },
                                    { status: 'MULTI TENANCY FORM COMPLETED', desc: 'Multi Tenancy Form workflow completed', color: '#6F9911' },
                                    { status: 'REJECTED', desc: 'Request was rejected by approver', color: '#CE1B4E' },
                                    { status: 'WITHDRAWN', desc: 'Request was withdrawn by submitter', color: '#767676' },
                                    { status: 'ERROR', desc: 'Error occurred during provisioning', color: '#CE1B4E' },
                                    { status: 'FAILED-VM Creation', desc: 'VM creation failed on cloud platform', color: '#CE1B4E' },
                                    { status: 'FAILED-Setup', desc: 'Post-provisioning setup failed', color: '#CE1B4E' },
                                    { status: 'FAILED-Multi Tenancy Form Script', desc: 'Multi Tenancy Form script execution failed', color: '#CE1B4E' },
                                    { status: 'FAILED-Multi Tenancy Form Processing', desc: 'Multi Tenancy Form processing failed', color: '#CE1B4E' },
                                    { status: 'VM_CREATION_CANCELLED', desc: 'VM creation was cancelled by approver', color: '#767676' },
                                    { status: 'LINODE_INSTANCE_CREATION_CANCELLED', desc: 'Linode instance creation was cancelled', color: '#767676' }
                                ].map((item, idx) => (
                                    <div key={idx} style={{
                                        padding: '1rem',
                                        background: '#f8f9fa',
                                        borderRadius: '6px',
                                        border: `2px solid ${item.color}30`,
                                        borderLeft: `4px solid ${item.color}`
                                    }}>
                            <div style={{ 
                                            fontWeight: '600', 
                                            color: item.color, 
                                            fontSize: '0.95rem',
                                            marginBottom: '0.5rem',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '0.5rem'
                                        }}>
                                            <Icon name="circle" style={{ fontSize: '0.7em' }} />
                                            {item.status}
                                        </div>
                                        <div style={{ fontSize: '0.9rem', color: '#666', lineHeight: '1.5' }}>
                                            {item.desc}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                );

            case 'license-capacity':
                return (
                    <div>
                        <div style={{
                            marginBottom: '2rem',
                            paddingBottom: '1.5rem',
                            borderBottom: '2px solid rgba(218, 165, 32, 0.2)'
                        }}>
                            <Header as="h2" style={{
                                margin: 0,
                                color: '#b58105',
                                fontSize: '2rem',
                                fontWeight: '600',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                                lineHeight: '1.2'
                            }}>
                                <Icon name="key" style={{ flexShrink: 0, verticalAlign: 'middle' }} />
                                <span>License & Capacity Guide</span>
                            </Header>
                        </div>

                        <Message info style={{
                            borderRadius: '8px',
                            border: '1px solid rgba(53, 183, 244, 0.2)',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                            marginBottom: '1.5rem'
                        }}>
                            <Message.Header style={{ fontWeight: '600' }}>At a Glance</Message.Header>
                            <List bulleted relaxed style={{ marginTop: '0.5rem', marginLeft: '1.5rem' }}>
                                <List.Item style={{ padding: '0.3rem 0', fontSize: '0.95rem', lineHeight: '1.6' }}>
                                    <strong>License Generator</strong> is available to users with the <strong>LICENSE_GENERATOR</strong> role.
                                </List.Item>
                                <List.Item style={{ padding: '0.3rem 0', fontSize: '0.95rem', lineHeight: '1.6' }}>
                                    <strong>Expiring Licenses</strong> is available to users with the <strong>CAPACITY_READER</strong> role.
                                </List.Item>
                                <List.Item style={{ padding: '0.3rem 0', fontSize: '0.95rem', lineHeight: '1.6' }}>
                                    If you cannot see a tab, contact DevOps/Admin to verify your role assignment.
                                </List.Item>
                            </List>
                        </Message>

                        <div style={{
                            marginBottom: '1.5rem',
                            padding: '1.5rem',
                            background: '#ffffff',
                            borderRadius: '8px',
                            border: '1px solid rgba(0,0,0,0.06)',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                        }}>
                            <Header as="h3" style={{
                                marginTop: 0,
                                marginBottom: '1rem',
                                color: '#062D4D',
                                fontSize: '1.25rem',
                                fontWeight: '600'
                            }}>
                                License Generator
                            </Header>
                            <p style={{ marginTop: 0, color: '#062D4D', lineHeight: '1.7' }}>
                                Use the License Generator tab to create and manage tenantBridge license outputs with validation and review controls before generation.
                            </p>
                            <List ordered relaxed style={{ marginTop: '0.5rem', marginLeft: '1.5rem' }}>
                                {[
                                    'Open the "License Generator" tab from the main navigation.',
                                    'Complete the required form inputs and review optional fields based on your scenario.',
                                    'Use the generated summary to verify key values before final generation.',
                                    'Generate and download the license output.',
                                    'Use License History to search by cloud name with Search and Clear controls.',
                                    'Download the latest or selected historical license file when needed.'
                                ].map((item, i) => (
                                    <List.Item key={i} style={{ padding: '0.35rem 0', fontSize: '0.95rem', lineHeight: '1.6' }}>
                                        {item}
                                    </List.Item>
                                ))}
                            </List>
                        </div>

                        <div style={{
                            marginBottom: '1.5rem',
                            padding: '1.5rem',
                            background: '#ffffff',
                            borderRadius: '8px',
                            border: '1px solid rgba(0,0,0,0.06)',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                        }}>
                            <Header as="h3" style={{
                                marginTop: 0,
                                marginBottom: '1rem',
                                color: '#062D4D',
                                fontSize: '1.25rem',
                                fontWeight: '600'
                            }}>
                                Expiring Licenses
                            </Header>
                            <p style={{ marginTop: 0, color: '#062D4D', lineHeight: '1.7' }}>
                                Use the Expiring Licenses tab to monitor validity and storage-related risk indicators, and to operate on available file actions.
                            </p>
                            <List ordered relaxed style={{ marginTop: '0.5rem', marginLeft: '1.5rem' }}>
                                {[
                                    'Open the "Expiring Licenses" tab from the main navigation.',
                                    'Review table records for validity and capacity-related indicators.',
                                    'Search by cloud name using Search, and reset results with Clear.',
                                    'Download the available expiring-license export file when needed.',
                                    'Import a supported file when refresh/upload operations are required.',
                                    'Use pagination and filters to focus on the required records.'
                                ].map((item, i) => (
                                    <List.Item key={i} style={{ padding: '0.35rem 0', fontSize: '0.95rem', lineHeight: '1.6' }}>
                                        {item}
                                    </List.Item>
                                ))}
                            </List>
                        </div>

                        <Grid stackable columns={2} style={{ marginBottom: '1.5rem' }}>
                            <Grid.Column>
                                <Segment style={{
                                    borderRadius: '8px',
                                    border: '1px solid rgba(53, 183, 244, 0.2)',
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                                    height: '100%'
                                }}>
                                    <Header as="h4" style={{ marginTop: 0, color: '#062D4D', fontWeight: '600' }}>
                                        Troubleshooting
                                    </Header>
                                    <List bulleted relaxed style={{ marginLeft: '1.5rem', marginTop: '0.5rem' }}>
                                        {[
                                            'Tab not visible: verify the required role is assigned.',
                                            'Search returns no results: confirm exact cloud name and clear prior filters.',
                                            'Download/import fails: retry after validating file type and network/API availability.',
                                            'Unexpected data gaps: refresh and re-run search before escalating.'
                                        ].map((item, i) => (
                                            <List.Item key={i} style={{ padding: '0.3rem 0', fontSize: '0.95rem', lineHeight: '1.6' }}>
                                                {item}
                                            </List.Item>
                                        ))}
                                    </List>
                                </Segment>
                            </Grid.Column>
                            <Grid.Column>
                                <Segment style={{
                                    borderRadius: '8px',
                                    border: '1px solid rgba(111, 153, 17, 0.25)',
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                                    height: '100%'
                                }}>
                                    <Header as="h4" style={{ marginTop: 0, color: '#062D4D', fontWeight: '600' }}>
                                        Operational Best Practices
                                    </Header>
                                    <List bulleted relaxed style={{ marginLeft: '1.5rem', marginTop: '0.5rem' }}>
                                        {[
                                            'Validate key values before generating licenses.',
                                            'Use consistent cloud naming for reliable history searches.',
                                            'Review expiring-license indicators on a regular cadence.',
                                            'Capture cloud name, timestamp, and error message before raising support requests.'
                                        ].map((item, i) => (
                                            <List.Item key={i} style={{ padding: '0.3rem 0', fontSize: '0.95rem', lineHeight: '1.6' }}>
                                                {item}
                                            </List.Item>
                                        ))}
                                    </List>
                                </Segment>
                            </Grid.Column>
                        </Grid>
                    </div>
                );

            case 'release-notes':
                return (
                    <div>
                        <div style={{ 
                            marginBottom: '2rem',
                            paddingBottom: '1.5rem',
                            borderBottom: '2px solid rgba(224, 57, 151, 0.15)'
                        }}>
                            <Header as="h2" style={{ 
                                margin: 0, 
                                color: '#e03997',
                                fontSize: '2rem',
                                fontWeight: '600',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                                lineHeight: '1.2'
                            }}>
                                <Icon name="newspaper" style={{ flexShrink: 0, verticalAlign: 'middle' }} />
                                <span>Release Notes</span>
                            </Header>
                        </div>

                        {/* v3.1.1 Release - Latest */}
                        <div style={{ 
                            marginBottom: '2rem',
                            padding: '1.5rem',
                            background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
                            borderRadius: '8px',
                            border: '2px solid rgba(53, 183, 244, 0.3)',
                            boxShadow: '0 4px 12px rgba(53, 183, 244, 0.15)'
                        }}>
                            <div style={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: '10px',
                                marginBottom: '1rem',
                                paddingBottom: '0.75rem',
                                borderBottom: '2px solid rgba(53, 183, 244, 0.2)'
                            }}>
                                <Header as="h3" style={{ margin: 0, color: '#062D4D', fontSize: '1.5rem', fontWeight: '600' }}>
                                    Version 3.1.1
                                </Header>
                                <Label color="blue" size="small" style={{ fontWeight: '600' }}>Latest</Label>
                                <Label color="teal" size="small" style={{ fontWeight: '600' }}>Bug Fix Release</Label>
                                <span style={{ color: '#666', fontSize: '0.9rem', marginLeft: 'auto', fontWeight: '500' }}>
                                    Released: TBD
                                </span>
                            </div>

                            <div style={{ 
                                marginBottom: '1.25rem', 
                                padding: '1rem', 
                                background: '#ffffff', 
                                borderRadius: '6px',
                                borderLeft: '4px solid #062D4D',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                            }}>
                                <p style={{ margin: 0, fontWeight: '600', color: '#062D4D', fontSize: '1.05rem' }}>
                                    Stability Update: Edit Request Fixes &amp; Structured Logging Improvements
                                </p>
                                <p style={{ color: '#666', marginTop: '0.5rem', margin: '0.5rem 0 0 0', fontSize: '0.95rem', lineHeight: '1.6' }}>
                                    Version 3.1.1 focuses on bug fixes for edit request behavior and improved log storage for ST, MT, and License Generator workflows.
                                </p>
                            </div>

                            <Header as="h4" style={{ 
                                marginTop: '0.5rem', 
                                marginBottom: '1rem', 
                                color: '#062D4D',
                                fontSize: '1.15rem',
                                fontWeight: '600',
                                paddingBottom: '0.5rem',
                                borderBottom: '1px solid rgba(0,0,0,0.06)'
                            }}>
                                Bug Fixes
                            </Header>

                            <List bulleted relaxed style={{ marginLeft: '1.5rem', marginTop: '0.25rem' }}>
                                <List.Item style={{ padding: '0.3rem 0', fontSize: '0.95rem', lineHeight: '1.6' }}>
                                    Fixed category handling in edit mode for both <strong>Single Tenancy</strong> and <strong>Multi Tenancy</strong> forms so the selected category is shown and updated correctly.
                                </List.Item>
                                <List.Item style={{ padding: '0.3rem 0', fontSize: '0.95rem', lineHeight: '1.6' }}>
                                    Fixed an issue in <strong>Single Tenancy Edit Request</strong> where changing fields could unexpectedly reset other form values.
                                </List.Item>
                                <List.Item style={{ padding: '0.3rem 0', fontSize: '0.95rem', lineHeight: '1.6' }}>
                                    Updated cloud name validation to allow reusing names from requests with <strong>WITHDRAWN</strong> status.
                                </List.Item>
                                <List.Item style={{ padding: '0.3rem 0', fontSize: '0.95rem', lineHeight: '1.6' }}>
                                    Added structured backend log storage with dedicated folders: <strong>ST logs</strong>, <strong>MT logs</strong>, and <strong>License Generation Logs</strong>; logs are now persisted to both local backend logs and mounted host logs path.
                                </List.Item>
                                <List.Item style={{ padding: '0.3rem 0', fontSize: '0.95rem', lineHeight: '1.6' }}>
                                    Improved visibility in <strong>Multi Tenancy Request Details</strong> by displaying the provisioned <strong>Cloud Name</strong> clearly once tenant creation is completed and <strong>cloudName</strong> is returned by provisioning.
                                </List.Item>
                            </List>
                        </div>

                        <Divider style={{ marginTop: '1.5rem', marginBottom: '1.5rem' }} />

                        {/* v3.1.0 Release */}
                        <div style={{ 
                            marginBottom: '2rem',
                            padding: '1.5rem',
                            background: '#ffffff',
                            borderRadius: '8px',
                            border: '1px solid rgba(0,0,0,0.06)',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                        }}>
                            <div style={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: '10px',
                                marginBottom: '1rem',
                                paddingBottom: '0.75rem',
                                borderBottom: '1px solid rgba(0,0,0,0.08)'
                            }}>
                                <Header as="h3" style={{ margin: 0, color: '#062D4D', fontSize: '1.5rem', fontWeight: '600' }}>
                                    Version 3.1.0
                                </Header>
                                <span style={{ color: '#666', fontSize: '0.9rem', marginLeft: 'auto', fontWeight: '500' }}>
                                    Released: April 02, 2026
                                </span>
                            </div>

                            <div style={{ 
                                marginBottom: '1.25rem', 
                                padding: '1rem', 
                                background: '#ffffff', 
                                borderRadius: '6px',
                                borderLeft: '4px solid #062D4D',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                            }}>
                                <p style={{ margin: 0, fontWeight: '600', color: '#062D4D', fontSize: '1.05rem' }}>
                                    Feature Update: License Generator, Expiring Licenses &amp; Multi-Tenancy Improvements
                                </p>
                                <p style={{ color: '#666', marginTop: '0.5rem', margin: '0.5rem 0 0 0', fontSize: '0.95rem', lineHeight: '1.6' }}>
                                    Version 3.1.0 introduces the new License Generator experience, Expiring Licenses enhancements, usability improvements in the Multi-Tenancy flow, and targeted bug fixes.
                                </p>
                            </div>

                            <Header as="h4" style={{ 
                                marginTop: '0.5rem', 
                                marginBottom: '1rem', 
                                color: '#062D4D',
                                fontSize: '1.15rem',
                                fontWeight: '600',
                                paddingBottom: '0.5rem',
                                borderBottom: '1px solid rgba(0,0,0,0.06)'
                            }}>
                                New Features
                            </Header>

                            <div style={{ marginBottom: '1.25rem' }}>
                                <Header as="h5" style={{ 
                                    marginTop: '0', 
                                    marginBottom: '0.5rem', 
                                    fontSize: '1rem', 
                                    fontWeight: '600',
                                    color: '#062D4D'
                                }}>
                                    License Generator
                                </Header>
                                <List bulleted relaxed style={{ marginLeft: '1.5rem', marginTop: '0.25rem' }}>
                                    <List.Item style={{ padding: '0.3rem 0', fontSize: '0.95rem', lineHeight: '1.6' }}>
                                        Introduced a new License Generator experience to streamline generation of tenantBridge licenses.
                                    </List.Item>
                                    <List.Item style={{ padding: '0.3rem 0', fontSize: '0.95rem', lineHeight: '1.6' }}>
                                        Access is role-based and available to users with the <strong>LICENSE_GENERATOR</strong> role.
                                    </List.Item>
                                    <List.Item style={{ padding: '0.3rem 0', fontSize: '0.95rem', lineHeight: '1.6' }}>
                                        Provides a guided form with validation and clear summaries before license creation.
                                    </List.Item>
                                    <List.Item style={{ padding: '0.3rem 0', fontSize: '0.95rem', lineHeight: '1.6' }}>
                                        License history supports search by cloud name using Search and Clear controls, consistent with the Expiring Licenses tab.
                                    </List.Item>
                                </List>
                            </div>

                            <div style={{ marginBottom: '1.25rem' }}>
                                <Header as="h5" style={{ 
                                    marginTop: '0', 
                                    marginBottom: '0.5rem', 
                                    fontSize: '1rem', 
                                    fontWeight: '600',
                                    color: '#062D4D'
                                }}>
                                    Expiring Licenses
                                </Header>
                                <List bulleted relaxed style={{ marginLeft: '1.5rem', marginTop: '0.25rem' }}>
                                    <List.Item style={{ padding: '0.3rem 0', fontSize: '0.95rem', lineHeight: '1.6' }}>
                                        Users with the <strong>CAPACITY_READER</strong> role can open the <strong>Expiring Licenses</strong> tab to review storage and validity details, search by cloud name, and download or import data as supported by the view.
                                    </List.Item>
                                </List>
                            </div>

                            <Header as="h4" style={{ 
                                marginTop: '0.5rem', 
                                marginBottom: '1rem', 
                                color: '#062D4D',
                                fontSize: '1.15rem',
                                    fontWeight: '600',
                                paddingBottom: '0.5rem',
                                borderBottom: '1px solid rgba(0,0,0,0.06)'
                                }}>
                                Bug Fixes
                                </Header>

                            <div style={{ marginBottom: '1.25rem' }}>
                                <Header as="h5" style={{ 
                                    marginTop: '0', 
                                    marginBottom: '0.5rem', 
                                    fontSize: '1rem', 
                                    fontWeight: '600',
                                    color: '#062D4D'
                                }}>
                                    Multi-Tenancy Form – View Details
                                </Header>
                                <List bulleted relaxed style={{ marginLeft: '1.5rem', marginTop: '0.25rem' }}>
                                    <List.Item style={{ padding: '0.3rem 0', fontSize: '0.95rem', lineHeight: '1.6' }}>
                                        Added a new field in the <strong>Multi-Tenancy Form &gt; View Details</strong> view to indicate whether the setup is a Proof of Concept (POC) with a clear <strong>Yes/No</strong> value.
                                    </List.Item>
                                </List>
                            </div>

                            {/* (Additional platform-wide improvements for timezone, badges, and UI/UX were introduced earlier in v3.0.0 and are documented in that section.) */}
                        </div>

                        <Divider style={{ marginTop: '1.5rem', marginBottom: '1.5rem' }} />

                        {/* v3.0.0 Release */}
                        <div style={{ 
                            marginBottom: '2rem',
                            padding: '1.5rem',
                            background: '#ffffff',
                            borderRadius: '8px',
                            border: '1px solid rgba(0,0,0,0.06)',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                        }}>
                            <div style={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: '10px',
                                marginBottom: '1rem',
                                paddingBottom: '0.75rem',
                                borderBottom: '1px solid rgba(0,0,0,0.08)'
                            }}>
                                <Header as="h3" style={{ margin: 0, color: '#062D4D', fontSize: '1.5rem', fontWeight: '600' }}>
                                    Version 3.0.0
                                </Header>
                                
                                <span style={{ color: '#666', fontSize: '0.9rem', marginLeft: 'auto', fontWeight: '500' }}>
                                    Released: February 11, 2026
                                </span>
                            </div>

                            <p style={{ margin: 0, color: '#555', lineHeight: '1.7', fontSize: '0.95rem' }}>
                                Introduced a major evolution of tenantBridge with real-time notifications, advanced filtering,
                                enhanced admin capabilities, and broader performance improvements across the platform.
                            </p>

                            <Header as="h4" style={{ 
                                marginTop: '1.25rem', 
                                marginBottom: '1rem', 
                                color: '#062D4D',
                                fontSize: '1.15rem',
                                fontWeight: '600',
                                paddingBottom: '0.5rem',
                                borderBottom: '1px solid rgba(0,0,0,0.06)'
                            }}>
                                Major Features
                            </Header>

                            <div style={{ marginBottom: '1.25rem' }}>
                                <Header as="h5" style={{ 
                                    marginTop: '0', 
                                    marginBottom: '0.5rem', 
                                    fontSize: '1rem', 
                                    fontWeight: '600',
                                    color: '#062D4D'
                                }}>
                                    Enhanced Database Performance
                                </Header>
                                <List bulleted relaxed style={{ marginLeft: '1.5rem', marginTop: '0.25rem' }}>
                                    <List.Item style={{ padding: '0.3rem 0', fontSize: '0.95rem', lineHeight: '1.6' }}>
                                        Database backend provides improved performance, reliability, and scalability.
                                    </List.Item>
                                    <List.Item style={{ padding: '0.3rem 0', fontSize: '0.95rem', lineHeight: '1.6' }}>
                                        Database indexes optimized for fast querying and filtering across all request data.
                                    </List.Item>
                                    <List.Item style={{ padding: '0.3rem 0', fontSize: '0.95rem', lineHeight: '1.6' }}>
                                        Enhanced data integrity with proper constraints and relationships.
                                    </List.Item>
                                    <List.Item style={{ padding: '0.3rem 0', fontSize: '0.95rem', lineHeight: '1.6' }}>
                                        Improved transaction handling and concurrent request processing.
                                    </List.Item>
                                </List>
                            </div>

                            <div style={{ marginBottom: '1.25rem' }}>
                                <Header as="h5" style={{ 
                                    marginTop: '0', 
                                    marginBottom: '0.5rem', 
                                    fontSize: '1rem', 
                                    fontWeight: '600',
                                    color: '#062D4D'
                                }}>
                                    Advanced Search &amp; Filtering
                                </Header>
                                <List bulleted relaxed style={{ marginLeft: '1.5rem', marginTop: '0.25rem' }}>
                                    <List.Item style={{ padding: '0.3rem 0', fontSize: '0.95rem', lineHeight: '1.6' }}>
                                        Global search functionality that searches across all fields simultaneously.
                                    </List.Item>
                                    <List.Item style={{ padding: '0.3rem 0', fontSize: '0.95rem', lineHeight: '1.6' }}>
                                        Multi-select filters for status, category, and other fields with visual badges.
                                    </List.Item>
                                    <List.Item style={{ padding: '0.3rem 0', fontSize: '0.95rem', lineHeight: '1.6' }}>
                                        Date range filtering for precise time-based queries.
                                    </List.Item>
                                    <List.Item style={{ padding: '0.3rem 0', fontSize: '0.95rem', lineHeight: '1.6' }}>
                                        Search term highlighting in table results for easy identification.
                                    </List.Item>
                                    <List.Item style={{ padding: '0.3rem 0', fontSize: '0.95rem', lineHeight: '1.6' }}>
                                        Save and load filter presets for frequently used search combinations.
                                    </List.Item>
                                </List>
                            </div>

                            <div style={{ marginBottom: '1.25rem' }}>
                                <Header as="h5" style={{ 
                                    marginTop: '0', 
                                    marginBottom: '0.5rem', 
                                    fontSize: '1rem', 
                                    fontWeight: '600',
                                    color: '#062D4D'
                                }}>
                                    Real-Time Notifications
                                </Header>
                                <List bulleted relaxed style={{ marginLeft: '1.5rem', marginTop: '0.25rem' }}>
                                    <List.Item style={{ padding: '0.3rem 0', fontSize: '0.95rem', lineHeight: '1.6' }}>
                                        Instant browser notifications when requests need your approval or status changes.
                                    </List.Item>
                                    <List.Item style={{ padding: '0.3rem 0', fontSize: '0.95rem', lineHeight: '1.6' }}>
                                        In-app notification center with unread count badge in the navbar.
                                    </List.Item>
                                    <List.Item style={{ padding: '0.3rem 0', fontSize: '0.95rem', lineHeight: '1.6' }}>
                                        Real-time updates for VM provisioning progress and completion.
                                    </List.Item>
                                </List>
                            </div>

                            <div style={{ marginBottom: '1.25rem' }}>
                                <Header as="h5" style={{ 
                                    marginTop: '0', 
                                    marginBottom: '0.5rem', 
                                    fontSize: '1rem', 
                                    fontWeight: '600',
                                    color: '#062D4D'
                                }}>
                                    Database Management Tools
                                </Header>
                                <List bulleted relaxed style={{ marginLeft: '1.5rem', marginTop: '0.25rem' }}>
                                    <List.Item style={{ padding: '0.3rem 0', fontSize: '0.95rem', lineHeight: '1.6' }}>
                                        Bulk delete support for efficient database maintenance.
                                    </List.Item>
                                    <List.Item style={{ padding: '0.3rem 0', fontSize: '0.95rem', lineHeight: '1.6' }}>
                                        Secure deletion with confirmation dialogs to prevent accidental data loss.
                                    </List.Item>
                                </List>
                            </div>

                            <div style={{ marginBottom: '1.25rem' }}>
                                <Header as="h5" style={{ 
                                    marginTop: '0', 
                                    marginBottom: '0.5rem', 
                                    fontSize: '1rem', 
                                    fontWeight: '600',
                                    color: '#062D4D'
                                }}>
                                    Comprehensive Admin Panel
                                </Header>
                                <List bulleted relaxed style={{ marginLeft: '1.5rem', marginTop: '0.25rem' }}>
                                    <List.Item style={{ padding: '0.3rem 0', fontSize: '0.95rem', lineHeight: '1.6' }}>
                                        Add, edit, and delete users with role and category assignments.
                                    </List.Item>
                                    <List.Item style={{ padding: '0.3rem 0', fontSize: '0.95rem', lineHeight: '1.6' }}>
                                        User search and filtering by role for easier administration.
                                    </List.Item>
                                    <List.Item style={{ padding: '0.3rem 0', fontSize: '0.95rem', lineHeight: '1.6' }}>
                                        Statistics dashboard showing total users, roles distribution, and categories.
                                    </List.Item>
                                    <List.Item style={{ padding: '0.3rem 0', fontSize: '0.95rem', lineHeight: '1.6' }}>
                                        Auto-provisioning configuration per user with category-specific access control.
                                    </List.Item>
                                    <List.Item style={{ padding: '0.3rem 0', fontSize: '0.95rem', lineHeight: '1.6' }}>
                                        Real-time user list updates with pagination support.
                                    </List.Item>
                                </List>
                            </div>

                            <div style={{ marginBottom: '1.25rem' }}>
                                <Header as="h5" style={{ 
                                    marginTop: '0', 
                                    marginBottom: '0.5rem', 
                                    fontSize: '1rem', 
                                    fontWeight: '600',
                                    color: '#062D4D'
                                }}>
                                    All Events View
                                </Header>
                                <List bulleted relaxed style={{ marginLeft: '1.5rem', marginTop: '0.25rem' }}>
                                    <List.Item style={{ padding: '0.3rem 0', fontSize: '0.95rem', lineHeight: '1.6' }}>
                                        System-wide event tracking showing all application activities and user actions.
                                    </List.Item>
                                    <List.Item style={{ padding: '0.3rem 0', fontSize: '0.95rem', lineHeight: '1.6' }}>
                                        Comprehensive audit trail with timestamps, user emails, event types, and descriptions.
                                    </List.Item>
                                    <List.Item style={{ padding: '0.3rem 0', fontSize: '0.95rem', lineHeight: '1.6' }}>
                                        Basic filtering by event type, user email, request ID, form type, and cloud provider.
                                    </List.Item>
                                    <List.Item style={{ padding: '0.3rem 0', fontSize: '0.95rem', lineHeight: '1.6' }}>
                                        Pagination support for efficient browsing of large event logs.
                                    </List.Item>
                                    <List.Item style={{ padding: '0.3rem 0', fontSize: '0.95rem', lineHeight: '1.6' }}>
                                        Event metadata includes category, cloud name, and request details for context.
                                    </List.Item>
                                    <List.Item style={{ padding: '0.3rem 0', fontSize: '0.95rem', lineHeight: '1.6' }}>
                                        Color-coded event types with icons for quick visual identification.
                                    </List.Item>
                                </List>
                            </div>

                            <div style={{ marginBottom: '1.25rem' }}>
                                <Header as="h5" style={{ 
                                    marginTop: '0', 
                                    marginBottom: '0.5rem', 
                                    fontSize: '1rem', 
                                    fontWeight: '600',
                                    color: '#062D4D'
                                }}>
                                    Auto Provisioning
                                </Header>
                                <List bulleted relaxed style={{ marginLeft: '1.5rem', marginTop: '0.25rem' }}>
                                    <List.Item style={{ padding: '0.3rem 0', fontSize: '0.95rem', lineHeight: '1.6' }}>
                                        Self-service provisioning workflow for authorized users and categories.
                                    </List.Item>
                                    <List.Item style={{ padding: '0.3rem 0', fontSize: '0.95rem', lineHeight: '1.6' }}>
                                        Users can submit and approve their own requests for designated categories.
                                    </List.Item>
                                    <List.Item style={{ padding: '0.3rem 0', fontSize: '0.95rem', lineHeight: '1.6' }}>
                                        Configurable per-user with category-specific access control via Admin Panel.
                                    </List.Item>
                                    <List.Item style={{ padding: '0.3rem 0', fontSize: '0.95rem', lineHeight: '1.6' }}>
                                        Streamlined approval process allows self-approval for authorized users.
                                    </List.Item>
                                    <List.Item style={{ padding: '0.3rem 0', fontSize: '0.95rem', lineHeight: '1.6' }}>
                                        Visual "auto" badge indicators distinguish auto-provisioning requests.
                                    </List.Item>
                                    <List.Item style={{ padding: '0.3rem 0', fontSize: '0.95rem', lineHeight: '1.6' }}>
                                        Direct subscription selection and VM provisioning after self-approval.
                                    </List.Item>
                                </List>
                            </div>

                            <div style={{ marginBottom: '1.25rem' }}>
                                <Header as="h5" style={{ 
                                    marginTop: '0', 
                                    marginBottom: '0.5rem', 
                                    fontSize: '1rem', 
                                    fontWeight: '600',
                                    color: '#062D4D'
                                }}>
                                    Timezone Support
                                </Header>
                                <List bulleted relaxed style={{ marginLeft: '1.5rem', marginTop: '0.25rem' }}>
                                    <List.Item style={{ padding: '0.3rem 0', fontSize: '0.95rem', lineHeight: '1.6' }}>
                                        User-configurable timezone preferences in Settings for personalized date/time display.
                                    </List.Item>
                                    <List.Item style={{ padding: '0.3rem 0', fontSize: '0.95rem', lineHeight: '1.6' }}>
                                        Support for multiple timezones including UTC, EST, PST, IST, and more.
                                    </List.Item>
                                    <List.Item style={{ padding: '0.3rem 0', fontSize: '0.95rem', lineHeight: '1.6' }}>
                                        All timestamps throughout the application automatically converted to user's selected timezone.
                                    </List.Item>
                                    <List.Item style={{ padding: '0.3rem 0', fontSize: '0.95rem', lineHeight: '1.6' }}>
                                        Live timezone clock display in Settings showing current time in selected timezone.
                                    </List.Item>
                                    <List.Item style={{ padding: '0.3rem 0', fontSize: '0.95rem', lineHeight: '1.6' }}>
                                        Timezone preferences persist across sessions and sync with backend.
                                    </List.Item>
                                </List>
                            </div>

                            <div style={{ marginBottom: '1.25rem' }}>
                                <Header as="h5" style={{ 
                                    marginTop: '0', 
                                    marginBottom: '0.5rem', 
                                    fontSize: '1rem', 
                                    fontWeight: '600',
                                    color: '#062D4D'
                                }}>
                                    Batch Visual Indicators
                                </Header>
                                <List bulleted relaxed style={{ marginLeft: '1.5rem', marginTop: '0.25rem' }}>
                                    <List.Item style={{ padding: '0.3rem 0', fontSize: '0.95rem', lineHeight: '1.6' }}>
                                        Clear "MT" badges on Multi Tenancy Form submissions in all approval views.
                                    </List.Item>
                                    <List.Item style={{ padding: '0.3rem 0', fontSize: '0.95rem', lineHeight: '1.6' }}>
                                        "auto" badges for auto-provisioning requests.
                                    </List.Item>
                                    <List.Item style={{ padding: '0.3rem 0', fontSize: '0.95rem', lineHeight: '1.6' }}>
                                        Improved visual distinction between standard requests and Multi Tenancy Form requests.
                                    </List.Item>
                                </List>
                            </div>

                            <div style={{ marginBottom: '1.25rem' }}>
                                <Header as="h5" style={{ 
                                    marginTop: '0', 
                                    marginBottom: '0.5rem', 
                                    fontSize: '1rem', 
                                    fontWeight: '600',
                                    color: '#062D4D'
                                }}>
                                    UI/UX Enhancements
                                </Header>
                                <List bulleted relaxed style={{ marginLeft: '1.5rem', marginTop: '0.25rem' }}>
                                    <List.Item style={{ padding: '0.3rem 0', fontSize: '0.95rem', lineHeight: '1.6' }}>
                                        Refined visual design with improved spacing, typography, and color consistency.
                                    </List.Item>
                                    <List.Item style={{ padding: '0.3rem 0', fontSize: '0.95rem', lineHeight: '1.6' }}>
                                        Enhanced loading states and empty state messages for better user feedback.
                                    </List.Item>
                                    <List.Item style={{ padding: '0.3rem 0', fontSize: '0.95rem', lineHeight: '1.6' }}>
                                        Smoother animations and transitions throughout the application.
                                    </List.Item>
                                </List>
                            </div>
                        </div>

                        <Divider style={{ marginTop: '1.5rem', marginBottom: '1.5rem' }} />

                        {/* v2.1.2 Release */}
                        <div style={{ 
                            marginBottom: '2rem',
                            padding: '1.5rem',
                            background: '#ffffff',
                            borderRadius: '8px',
                            border: '1px solid rgba(0, 0, 0, 0.1)',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                        }}>
                            <div style={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: '10px',
                                marginBottom: '1rem',
                                paddingBottom: '0.75rem',
                                borderBottom: '1px solid rgba(0,0,0,0.08)'
                            }}>
                                <Header as="h3" style={{ margin: 0, color: '#666', fontSize: '1.5rem', fontWeight: '600' }}>
                                    Version 2.1.2
                                </Header>
                                <span style={{ color: '#666', fontSize: '0.9rem', marginLeft: 'auto', fontWeight: '500' }}>
                                    Released: November 28, 2025
                                </span>
                            </div>

                            <Header as="h4" style={{ 
                                marginTop: '0.5rem', 
                                marginBottom: '1rem', 
                                color: '#062D4D',
                                fontSize: '1.15rem',
                                fontWeight: '600',
                                paddingBottom: '0.5rem',
                                borderBottom: '1px solid rgba(0,0,0,0.08)'
                            }}>
                                Bug Fixes & Enhancements
                            </Header>

                            <div style={{ marginBottom: '1.25rem' }}>
                                <Header as="h5" style={{ 
                                    marginTop: '0', 
                                    marginBottom: '0.5rem', 
                                    fontSize: '1rem', 
                                    fontWeight: '600',
                                    color: '#062D4D'
                                }}>
                                    Fixed Edit Request Functionality
                                </Header>
                                <List bulleted relaxed style={{ marginLeft: '1.5rem', marginTop: '0.25rem' }}>
                                    <List.Item style={{ padding: '0.3rem 0', fontSize: '0.95rem', lineHeight: '1.6' }}>
                                        Fixed issue where edit form was not pre-populating with existing request data.
                                    </List.Item>
                                    <List.Item style={{ padding: '0.3rem 0', fontSize: '0.95rem', lineHeight: '1.6' }}>
                                        All form fields now correctly display existing values when editing a request.
                                    </List.Item>
                                    <List.Item style={{ padding: '0.3rem 0', fontSize: '0.95rem', lineHeight: '1.6' }}>
                                        "Decide your URL" field is now properly populated but remains disabled during edit (as intended).
                                    </List.Item>
                                    <List.Item style={{ padding: '0.3rem 0', fontSize: '0.95rem', lineHeight: '1.6' }}>
                                        Added support for Germany West Central location in edit mode.
                                    </List.Item>
                                </List>
                            </div>

                            <div style={{ marginBottom: '1.25rem' }}>
                                <Header as="h5" style={{ 
                                    marginTop: '0', 
                                    marginBottom: '0.5rem', 
                                    fontSize: '1rem', 
                                    fontWeight: '600',
                                    color: '#062D4D'
                                }}>
                                    Multi Tenancy Form Edit Support
                                </Header>
                                <List bulleted relaxed style={{ marginLeft: '1.5rem', marginTop: '0.25rem' }}>
                                    <List.Item style={{ padding: '0.3rem 0', fontSize: '0.95rem', lineHeight: '1.6' }}>
                                        Multi Tenancy Form submissions can now be edited before approver approval.
                                    </List.Item>
                                    <List.Item style={{ padding: '0.3rem 0', fontSize: '0.95rem', lineHeight: '1.6' }}>
                                        Edit modal automatically detects Multi Tenancy Form requests and uses the appropriate form component.
                                    </List.Item>
                                    <List.Item style={{ padding: '0.3rem 0', fontSize: '0.95rem', lineHeight: '1.6' }}>
                                        All Multi Tenancy Form fields are properly mapped and pre-populated during edit.
                                    </List.Item>
                                </List>
                            </div>
                        </div>

                        <Divider style={{ marginTop: '1.5rem', marginBottom: '1.5rem' }} />

                        {/* v2.1.1 Release */}
                        <div style={{ 
                            marginBottom: '2rem',
                            padding: '1.5rem',
                            background: '#ffffff',
                            borderRadius: '8px',
                            border: '1px solid rgba(0,0,0,0.06)',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                        }}>
                            <div style={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: '10px',
                                marginBottom: '1rem',
                                paddingBottom: '0.75rem',
                                borderBottom: '1px solid rgba(0,0,0,0.08)'
                            }}>
                                <Header as="h3" style={{ margin: 0, color: '#666', fontSize: '1.3rem', fontWeight: '600' }}>
                                    Version 2.1.1
                                </Header>
                                <span style={{ color: '#666', fontSize: '0.9rem', marginLeft: 'auto', fontWeight: '500' }}>
                                    Released: November 24, 2025
                                </span>
                            </div>

                            <Header as="h4" style={{ 
                                marginTop: '0.5rem', 
                                marginBottom: '1rem', 
                                color: '#062D4D',
                                fontSize: '1.15rem',
                                fontWeight: '600',
                                paddingBottom: '0.5rem',
                                borderBottom: '1px solid rgba(0,0,0,0.06)'
                            }}>
                                Enhancements
                            </Header>

                            {[
                                {
                                    title: 'View Details in Approval History',
                                    items: [
                                        'Added "View Details" button to "Approval History" page for easy access to full request information.',
                                        'Added "View Details" button to "All Approval History" page.',
                                        'Users can now view complete request details directly from history tables without navigating away.'
                                    ]
                                },
                                {
                                    title: 'Improved Copy Functionality',
                                    items: [
                                        'Fixed copy button for "Decide your URL" Portal URL field with proper async handling and error management.',
                                        'Added visual feedback with icon change and toast notification when URL is copied successfully.',
                                        'Copy notifications now appear as toast messages in the top-right corner for better visibility.'
                                    ]
                                },
                                {
                                    title: 'Enhanced Setup Type Icons',
                                    items: [
                                        'Updated Multi Tenancy Setup icon to "users" icon for better visual representation.',
                                        'Updated Single Tenant Setup icon to "user" icon to match the multi-tenancy icon style.',
                                        'Icons now provide clearer visual distinction between setup types in request details.'
                                    ]
                                }
                            ].map((feature, idx) => (
                                <div key={idx} style={{ marginBottom: '1.25rem' }}>
                                    <Header as="h5" style={{ 
                                        marginTop: idx === 0 ? '0' : '0', 
                                        marginBottom: '0.5rem', 
                                        fontSize: '1rem', 
                                        fontWeight: '600',
                                        color: '#062D4D'
                                    }}>
                                        {feature.title}
                                    </Header>
                                    <List bulleted relaxed style={{ marginLeft: '1.5rem', marginTop: '0.25rem' }}>
                                        {feature.items.map((item, i) => (
                                            <List.Item key={i} style={{ padding: '0.3rem 0', fontSize: '0.95rem', lineHeight: '1.6' }}>
                                                {item}
                                            </List.Item>
                                        ))}
                                    </List>
                                </div>
                            ))}
                        </div>

                        <Divider style={{ marginTop: '1.5rem', marginBottom: '1.5rem' }} />

                        {/* v2.1.0 Release */}
                        <div style={{ 
                            marginBottom: '2rem',
                            padding: '1.5rem',
                            background: '#ffffff',
                            borderRadius: '8px',
                            border: '1px solid rgba(0,0,0,0.06)',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                        }}>
                            <div style={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: '10px',
                                marginBottom: '1rem',
                                paddingBottom: '0.75rem',
                                borderBottom: '1px solid rgba(0,0,0,0.08)'
                            }}>
                                <Header as="h3" style={{ margin: 0, color: '#666', fontSize: '1.3rem', fontWeight: '600' }}>
                                    Version 2.1.0
                                </Header>
                                <span style={{ color: '#666', fontSize: '0.9rem', marginLeft: 'auto', fontWeight: '500' }}>
                                    Released: November 20, 2025
                                </span>
                            </div>

                            <Header as="h4" style={{ 
                                marginTop: '0.5rem', 
                                marginBottom: '1rem', 
                                color: '#062D4D',
                                fontSize: '1.15rem',
                                fontWeight: '600',
                                paddingBottom: '0.5rem',
                                borderBottom: '1px solid rgba(0,0,0,0.06)'
                            }}>
                                New Features
                            </Header>

                            {[
                                {
                                    title: 'Enhanced Approval Workflow',
                                    items: [
                                        'Approve and Reject buttons have been moved into the "View Details" modal for a cleaner interface.',
                                        'Approvers can now take action directly from the details view.',
                                        'Improved state management ensures buttons remain functional after approval actions.'
                                    ]
                                },
                                {
                                    title: 'Filtering Enhancements',
                                    items: [
                                        'Added basic filter options to "My Submitted Requests" page.',
                                        'Filter by Status, Cloud Name, Category, and Request ID.',
                                        'Advanced filters with global search available in Approval History and All Approval History views.',
                                        'Basic filters available in My Submitted Requests and All Events views.'
                                    ]
                                }
                            ].map((feature, idx) => (
                                <div key={idx} style={{ marginBottom: '1.25rem' }}>
                                    <Header as="h5" style={{ 
                                        marginTop: idx === 0 ? '0' : '0', 
                                        marginBottom: '0.5rem', 
                                        fontSize: '1rem', 
                                        fontWeight: '600',
                                        color: '#062D4D'
                                    }}>
                                        {feature.title}
                                    </Header>
                                    <List bulleted relaxed style={{ marginLeft: '1.5rem', marginTop: '0.25rem' }}>
                                        {feature.items.map((item, i) => (
                                            <List.Item key={i} style={{ padding: '0.3rem 0', fontSize: '0.95rem', lineHeight: '1.6' }}>
                                                {item}
                                            </List.Item>
                                        ))}
                                    </List>
                                </div>
                            ))}

                            <Divider style={{ marginTop: '1.25rem', marginBottom: '1rem' }} />

                            <Header as="h4" style={{ 
                                marginTop: '0', 
                                marginBottom: '1rem', 
                                color: '#062D4D',
                                fontSize: '1.15rem',
                                fontWeight: '600',
                                paddingBottom: '0.5rem',
                                borderBottom: '1px solid rgba(0,0,0,0.06)'
                            }}>
                                UI/UX Enhancements
                            </Header>

                            {[
                                {
                                    title: 'Improved Table Navigation',
                                    items: [
                                        'Pagination is now sticky/static across all views (My Submitted Requests, Approval History, All Approval History, Approver View).',
                                        'Table headers remain visible while scrolling for better context.',
                                        'Headers, filters, and pagination stay fixed while only table content scrolls.',
                                        'Action buttons no longer overlap with headers during scrolling.'
                                    ]
                                },
                                {
                                    title: 'Release Notes Access',
                                    content: 'Version information in the profile menu is now clickable, allowing users to easily access release notes and stay informed about updates.'
                                },
                                {
                                    title: 'Enhanced Email Notifications',
                                    items: [
                                        'Portal Information section added to all instance creation emails (Azure, Linode, and Multi Tenancy Form).',
                                        'Portal URLs are now dynamically generated and displayed in completion emails.',
                                        'Streamlined email templates by removing redundant buttons for a cleaner design.'
                                    ]
                                }
                            ].map((feature, idx) => (
                                <div key={idx} style={{ marginBottom: '1.25rem' }}>
                                    <Header as="h5" style={{ 
                                        marginTop: idx === 0 ? '0' : '0', 
                                        marginBottom: '0.5rem', 
                                        fontSize: '1rem', 
                                        fontWeight: '600',
                                        color: '#062D4D'
                                    }}>
                                        {feature.title}
                                    </Header>
                                    {feature.items ? (
                                        <List bulleted relaxed style={{ marginLeft: '1.5rem', marginTop: '0.25rem' }}>
                                            {feature.items.map((item, i) => (
                                                <List.Item key={i} style={{ padding: '0.3rem 0', fontSize: '0.95rem', lineHeight: '1.6' }}>
                                                    {item}
                                                </List.Item>
                                            ))}
                                        </List>
                                    ) : (
                                        <p style={{ marginLeft: '1.5rem', color: '#555', lineHeight: '1.7', fontSize: '0.95rem', marginTop: '0.25rem' }}>
                                            {feature.content}
                                        </p>
                                    )}
                                </div>
                            ))}
                        </div>

                        <Divider style={{ marginTop: '1.5rem', marginBottom: '1.5rem' }} />

                        {/* v2.0.0 Release */}
                        <div style={{ 
                            marginBottom: '2rem',
                            padding: '1.5rem',
                            background: '#ffffff',
                            borderRadius: '8px',
                            border: '1px solid rgba(0,0,0,0.06)',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                        }}>
                            <div style={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: '10px',
                                marginBottom: '1rem',
                                paddingBottom: '0.75rem',
                                borderBottom: '1px solid rgba(0,0,0,0.08)'
                            }}>
                                <Header as="h3" style={{ margin: 0, color: '#666', fontSize: '1.3rem', fontWeight: '600' }}>
                                    Version 2.0.0
                                </Header>
                                <span style={{ color: '#666', fontSize: '0.9rem', marginLeft: 'auto', fontWeight: '500' }}>
                                    Released: November 4, 2025
                                </span>
                            </div>

                            <div style={{ 
                                marginBottom: '1.25rem', 
                                padding: '1rem', 
                                background: '#ffffff', 
                                borderRadius: '6px',
                                borderLeft: '3px solid #062D4D'
                            }}>
                                <p style={{ margin: 0, fontWeight: '500', color: '#062D4D' }}>
                                    From now on, this application will be known as <strong>tenantBridge</strong>.
                                </p>
                                <p style={{ color: '#666', marginTop: '0.5rem', margin: '0.5rem 0 0 0', fontSize: '0.9rem', lineHeight: '1.6' }}>
                                    We chose "tenantBridge" because it connects tenants, teams, and cloud providers—bringing requests, approvals, and provisioning into one streamlined flow.
                                </p>
                            </div>

                            <Header as="h4" style={{ 
                                marginTop: '0', 
                                marginBottom: '1rem', 
                                color: '#062D4D',
                                fontSize: '1.15rem',
                                fontWeight: '600',
                                paddingBottom: '0.5rem',
                                borderBottom: '1px solid rgba(0,0,0,0.06)'
                            }}>
                                New Features
                            </Header>

                            {[
                                {
                                    title: 'Enhanced "My Submitted Requests" Page',
                                    items: [
                                        'Requesters can now view the full details of their submitted forms.',
                                        'Requesters can also withdraw their submissions.',
                                        'If a requester tries to withdraw a form after it has been approved by an approver, a confirmation message will appear notifying them that it\'s already approved, asking if they still wish to proceed.',
                                        'Every withdrawal action now requires a reason for better tracking.'
                                    ]
                                },
                                {
                                    title: 'Approver Control over Resource Creation',
                                    content: 'Approvers can now cancel ongoing operations during Azure or Linode resource creation processes.'
                                },
                                {
                                    title: 'Edit and Resubmit Forms',
                                    content: 'Requesters can now edit and resubmit their submitted forms directly from the "My Submitted Requests" page, except for the "Decide your URL" field.'
                                },
                                {
                                    title: 'Preferences and Table Controls',
                                    items: [
                                        'New Preferences in the profile menu: Table Density (Comfortable/Compact) and Rows Per Page (25/50/75/100).',
                                        'Pagination added to My Submitted Requests, Approval History, and All Approval History.',
                                        'Preferences persist across sessions and update all tables instantly.'
                                    ]
                                }
                            ].map((feature, idx) => (
                                <div key={idx} style={{ marginBottom: '1.25rem' }}>
                                    <Header as="h5" style={{ 
                                        marginTop: idx === 0 ? '0' : '0', 
                                        marginBottom: '0.5rem', 
                                        fontSize: '1rem', 
                                        fontWeight: '600',
                                        color: '#062D4D'
                                    }}>
                                        {feature.title}
                                    </Header>
                                    {feature.items ? (
                                        <List bulleted relaxed style={{ marginLeft: '1.5rem', marginTop: '0.25rem' }}>
                                            {feature.items.map((item, i) => (
                                                <List.Item key={i} style={{ padding: '0.3rem 0', fontSize: '0.95rem', lineHeight: '1.6' }}>
                                                    {item}
                                                </List.Item>
                                            ))}
                                        </List>
                                    ) : (
                                        <p style={{ marginLeft: '1.5rem', color: '#555', lineHeight: '1.7', fontSize: '0.95rem', marginTop: '0.25rem' }}>
                                            {feature.content}
                                        </p>
                                    )}
                                </div>
                            ))}

                            <Divider style={{ marginTop: '1.25rem', marginBottom: '1rem' }} />

                            <Header as="h4" style={{ 
                                marginTop: '0', 
                                marginBottom: '1rem', 
                                color: '#062D4D',
                                fontSize: '1.15rem',
                                fontWeight: '600',
                                paddingBottom: '0.5rem',
                                borderBottom: '1px solid rgba(0,0,0,0.06)'
                            }}>
                                Enhancements and Updates
                            </Header>

                            {[
                                {
                                    title: 'Improved Request Form Details',
                                    content: 'The request form now shows: Current Status (with color-coded text), Migration Activity (Yes/No), Tenant Name, Approver Name and Status. The approver section now displays only the exact approver who handled the request, instead of listing all approvers.'
                                },
                                {
                                    title: 'Refined Rejection Process',
                                    content: 'Approvers can now add proper rejection comments, improving clarity and communication.'
                                },
                                {
                                    title: 'UI/UX Improvements',
                                    items: [
                                        'Redesigned Actions in My Submitted Requests with inline buttons and a compact three‑dots menu.',
                                        'Approver actions streamlined: View, Approve, Reject, plus a compact actions menu.'
                                    ]
                                },
                                {
                                    title: 'Improved Email Templates',
                                    content: 'Email notifications have been refined for clearer communication and better readability.'
                                }
                            ].map((feature, idx) => (
                                <div key={idx} style={{ marginBottom: '1.25rem' }}>
                                    <Header as="h5" style={{ 
                                        marginTop: idx === 0 ? '0' : '0', 
                                        marginBottom: '0.5rem', 
                                        fontSize: '1rem', 
                                        fontWeight: '600',
                                        color: '#062D4D'
                                    }}>
                                        {feature.title}
                                    </Header>
                                    {feature.items ? (
                                        <List bulleted relaxed style={{ marginLeft: '1.5rem', marginTop: '0.25rem' }}>
                                            {feature.items.map((item, i) => (
                                                <List.Item key={i} style={{ padding: '0.3rem 0', fontSize: '0.95rem', lineHeight: '1.6' }}>
                                                    {item}
                                                </List.Item>
                                            ))}
                                        </List>
                                    ) : (
                                        <p style={{ marginLeft: '1.5rem', color: '#555', lineHeight: '1.7', fontSize: '0.95rem', marginTop: '0.25rem' }}>
                                            {feature.content}
                                        </p>
                                    )}
                                </div>
                            ))}

                            <Divider style={{ marginTop: '1.25rem', marginBottom: '1rem' }} />

                            <Header as="h4" style={{ 
                                marginTop: '0', 
                                marginBottom: '1rem', 
                                color: '#062D4D',
                                fontSize: '1.15rem',
                                fontWeight: '600',
                                paddingBottom: '0.5rem',
                                borderBottom: '1px solid rgba(0,0,0,0.06)'
                            }}>
                                Upcoming Enhancements
                            </Header>

                            {[
                                {
                                    title: 'Automatic Keeper Record Sharing',
                                    content: 'Automatic record sharing for requesters is in progress and will be introduced in Version 3.0.'
                                },
                                {
                                    title: 'Tenant Deletion via tenantBridge',
                                    content: 'Soon, tenants can be deleted directly through the application.'
                                }
                            ].map((feature, idx) => (
                                <div key={idx} style={{ marginBottom: '1.25rem' }}>
                                    <Header as="h5" style={{ 
                                        marginTop: idx === 0 ? '0' : '0', 
                                        marginBottom: '0.5rem', 
                                        fontSize: '1rem', 
                                        fontWeight: '600',
                                        color: '#062D4D'
                                    }}>
                                        {feature.title}
                                    </Header>
                                    <p style={{ marginLeft: '1.5rem', color: '#555', lineHeight: '1.7', fontSize: '0.95rem', marginTop: '0.25rem' }}>
                                        {feature.content}
                                    </p>
                                </div>
                            ))}
                        </div>

                        <Message info style={{ 
                            borderRadius: '8px',
                            border: '1px solid rgba(53, 183, 244, 0.2)',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                            marginTop: '2rem'
                        }}>
                            <Message.Header style={{ fontWeight: '600' }}>Stay Updated</Message.Header>
                            <p style={{ marginTop: '0.5rem', marginBottom: 0, lineHeight: '1.7' }}>
                                New release notifications appear automatically as a toast notification when you log in. You can access release notes 
                                at any time from the Settings page under the User Manual section, or directly from this manual. The current version 
                                is also displayed in your profile dropdown menu.
                            </p>
                        </Message>
                    </div>
                );

            case 'faq':
                return (
                    <div>
                        <div style={{ 
                            marginBottom: '2rem',
                            paddingBottom: '1.5rem',
                            borderBottom: '2px solid rgba(118, 118, 118, 0.15)'
                        }}>
                            <Header as="h2" style={{ 
                                margin: 0, 
                                color: '#767676',
                                fontSize: '2rem',
                                fontWeight: '600',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                                lineHeight: '1.2'
                            }}>
                                <Icon name="question circle" style={{ flexShrink: 0, verticalAlign: 'middle' }} />
                                <span>Frequently Asked Questions</span>
                            </Header>
                        </div>

                        {[
                            {
                                category: 'General Questions',
                                questions: [
                                    {
                                        q: 'How do I get access to tenantBridge?',
                                        a: 'You need a CrashPlan Google account and must be assigned a role by your system administrator. Please contact your IT team to request access.'
                                    },
                                    {
                                        q: 'What roles are available?',
                                        a: 'There are three main roles: SINGLE TENANCY FORM, MULTI TENANCY FORM, and APPROVER. Your role determines what actions you can perform in the system.'
                                    },
                                    {
                                        q: 'Can I have multiple roles?',
                                        a: 'Yes, users can have multiple roles. For example, you might be both a SINGLE TENANCY FORM user and an APPROVER for different categories.'
                                    }
                                ]
                            },
                            {
                                category: 'Request Submission',
                                questions: [
                                    {
                                        q:                                     'What\'s the difference between Single Tenancy Form and Multi Tenancy Form?',
                                    a: 'The Multi Tenancy Form is an enhanced version with additional capabilities, including automatic tenant domain selection based on category, fixed storage options (Azure Blob or S3-Compatible), a streamlined workflow, and support for migrating from single-tenant to multi-tenant setups. It is available to users with the MULTI TENANCY FORM role.'
                                    },
                                    {
                                        q: 'How long does it take for a request to be approved?',
                                        a: 'Approval time depends on the approver\'s availability. Typically, requests are reviewed within 1–2 business days. You will receive email notifications at each stage of the process.'
                                    },
                                    {
                                        q: 'Can I edit a request after submission?',
                                        a: 'You can edit or withdraw requests that are still pending approval. Once a request has been approved or rejected, it can no longer be modified. Check the "My Submitted Requests" tab for available actions.'
                                    },
                                    {
                                        q: 'What happens if my request is rejected?',
                                        a: 'You will receive an email notification with the rejection reason. You can then submit a new request with the corrected information. The rejected request will remain in your history for reference.'
                                    }
                                ]
                            },
                            {
                                category: 'VM Provisioning',
                                questions: [
                                    {
                                    q: 'How long does VM provisioning take?',
                                    a: 'VM/Instance provisioning typically takes 5–15 minutes for Azure VMs and 3–10 minutes for Linode instances, depending on the cloud provider, instance size, and post-provisioning setup requirements. You will receive an email with credentials once provisioning is complete.'
                                    },
                                    {
                                    q: 'What if VM provisioning fails?',
                                    a: 'If provisioning fails, the request status changes to ERROR or a specific failure status (e.g., FAILED-VM Creation, FAILED-Setup). Check the request details for error information and logs. Contact your approver or system administrator for assistance — you may need to resubmit the request with corrected information.'
                                    },
                                    {
                                    q: 'Where are my VM credentials stored?',
                                    a: 'Credentials are sent via email upon successful VM/Instance creation. They are also stored securely in Keeper Security Manager (KSM) and can be accessed through the request details. Portal URLs and access information are included in the completion email. For PROD or CUST category requests (Single Tenancy), use the "Request Credentials via JIRA" button. For all other categories, use the "Access Credentials via Keeper" button to retrieve credentials from the Keeper vault.'
                                    }
                                ]
                            },
                            {
                                category: 'Approval Process',
                                questions: [
                                    {
                                        q: 'Can I see who approved my request?',
                                        a: 'Yes, the approval history shows all approvers, their actions, and timestamps. You can view this information in the request details modal.'
                                    }
                                ]
                            },
                            {
                                category: 'Search & Filtering',
                                questions: [
                                    {
                                        q: 'How do I use the advanced filters?',
                                        a: 'Advanced filters with global search are available in the Approval History and All Approval History views. Click the "Show Filters" button to open the sidebar filter panel. Use Global Search to search across all fields, or apply specific filters such as status, category, date ranges, or submitted by. Active filters are highlighted with blue borders. The My Submitted Requests and All Events views offer basic filters instead.'
                                    },
                                    {
                                        q: 'Can I save my filter settings?',
                                        a: 'Yes! After applying your desired filters, enter a name in the "Save as..." field at the bottom of the filter panel and click the save icon. You can then load these presets anytime from the "Saved" tab.'
                                    },
                                    {
                                        q: 'What is search term highlighting?',
                                        a: 'When you search for text, matching terms in the table results are highlighted in yellow. This makes it easy to quickly identify where your search terms appear in the data.'
                                    },
                                    {
                                        q: 'How do I clear all filters?',
                                        a: 'Click the "Clear All" button at the bottom of the filter panel, or click "Clear All Filters" if using the basic filter view. This resets all active filters at once.'
                                    }
                                ]
                            },
                            {
                                category: 'Account Management',
                                questions: [
                                    {
                                        q: 'How do I delete my account?',
                                        a: 'Go to Settings → Account tab and scroll to the "Danger Zone" section. Click "Delete My Account", type DELETE in the confirmation field, and click "Permanently Delete Account". You will be logged out immediately. A confirmation email is sent to you and a notification email is sent to all DevOps team members.'
                                    },
                                    {
                                        q: 'Can I recover my account after deletion?',
                                        a: 'Account deletion is permanent and cannot be undone by yourself. However, a DevOps administrator can re-add your account from the Admin Panel. Contact the tenantBridge team immediately if you deleted your account by mistake.'
                                    },
                                    {
                                        q: 'Who gets notified when I delete my account?',
                                        a: 'Two emails are sent: a confirmation email to your own email address, and a notification email to all users with the DEVOPS role informing them that your account has been removed.'
                                    }
                                ]
                            },
                            {
                                category: 'Credentials & Access',
                                questions: [
                                    {
                                        q: 'What is the "Request Credentials via JIRA" button?',
                                        a: 'This blue button appears in the Actions column for completed PROD or CUST category requests (Single Tenancy), or PROD non-POC requests (Multi Tenancy). Clicking it opens a pre-filled JIRA ticket creation page with your request details (Request ID, Cloud Name, Submitted By, Status). You can add additional context and submit the ticket to the DevOps team to receive your credentials.'
                                    },
                                    {
                                        q: 'What is the "Access Credentials via Keeper" button?',
                                        a: 'This green button appears for all other completed requests (e.g., STAGE, ENGG_DEV, ENGG_QA, DEMO, TESTING, POC categories, or PROD POC and CUST in Multi Tenancy). Clicking it opens the Keeper Security vault in a new tab where your VM/instance credentials are securely stored. Search for your cloud name or request ID to find your credentials.'
                                    },
                                    {
                                        q: 'When do the credential access buttons appear?',
                                        a: 'The JIRA and Keeper buttons only appear after a request has been successfully provisioned (status: COMPLETED, TENANT IS READY, or MULTI TENANCY FORM COMPLETED). While a request is still pending, you will see Withdraw and Edit buttons instead.'
                                    }
                                ]
                            },
                            {
                                category: 'Notifications',
                                questions: [
                                    {
                                        q: 'How do I enable browser notifications?',
                                        a: 'Click the bell icon in the navbar, then click "Enable Notifications" when prompted. You will then receive real-time updates for requests that require your attention.'
                                    },
                                    {
                                        q: 'What types of notifications will I receive?',
                                        a: 'You will receive notifications for new requests needing your approval, request status changes, VM provisioning updates, and other important events related to your role.'
                                    },
                                    {
                                        q: 'Can I disable notifications?',
                                        a: 'Yes, you can disable browser notifications through your browser settings. In-app notifications will still appear in the notification center.'
                                    }
                                ]
                            }
                        ].map((category, catIdx) => (
                            <div key={catIdx} style={{ marginBottom: '2rem' }}>
                                <Header as="h3" style={{ 
                                    marginBottom: '1rem',
                                    color: '#062D4D',
                                    fontSize: '1.4rem',
                                    fontWeight: '600',
                                    paddingBottom: '0.5rem',
                                    borderBottom: '1px solid rgba(0,0,0,0.08)'
                                }}>
                                    {category.category}
                                </Header>
                                {category.questions.map((qa, qIdx) => (
                                    <div key={qIdx} style={{ 
                                        marginBottom: '1.25rem',
                                        padding: '1.5rem',
                                        background: '#ffffff',
                                        borderRadius: '8px',
                                        border: '1px solid rgba(0,0,0,0.06)',
                                        boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                                    }}>
                                        <Header as="h4" style={{ 
                                            marginTop: 0,
                                            marginBottom: '0.75rem',
                                            color: '#062D4D',
                                            fontSize: '1.05rem',
                                            fontWeight: '600'
                                        }}>
                                            Q: {qa.q}
                                        </Header>
                                        <p style={{ 
                                            margin: 0,
                                            color: '#062D4D',
                                            lineHeight: '1.7',
                                            fontSize: '0.95rem'
                                        }}>
                                            <strong style={{ color: '#062D4D' }}>A:</strong> {qa.a}
                                        </p>
                                    </div>
                                ))}
                            </div>
                        ))}

                        <Message info style={{ 
                            borderRadius: '8px',
                            border: '1px solid rgba(53, 183, 244, 0.2)',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                        }}>
                            <Message.Header style={{ fontWeight: '600' }}>Need More Help?</Message.Header>
                            <p style={{ marginTop: '0.5rem', marginBottom: 0, lineHeight: '1.7' }}>
                                If you have additional questions or encounter any issues, please contact your system administrator 
                                or IT support team. You can also refer to the role-specific guides in this manual for more detailed instructions.
                            </p>
                        </Message>
                    </div>
                );

            default:
                return null;
        }
    };

    const activeSectionData = sections.find(s => s.key === activeSection);

    return (
        <div style={{ 
            minHeight: '100vh',
            background: '#f7f9fc',
            backgroundImage: 'radial-gradient(1200px 800px at 10% 0%, rgba(53, 183, 244, 0.04), transparent 60%), linear-gradient(to bottom right, #f7f9fc, #f2f6fb)',
            backgroundAttachment: 'fixed',
            padding: '2rem 0'
        }}>
            <Container style={{ maxWidth: '1400px' }}>
                {/* Header */}
                <div style={{ 
                    marginBottom: '2rem',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '1.5rem',
                    background: '#ffffff',
                    borderRadius: '8px',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                    border: '1px solid rgba(0,0,0,0.06)'
                }}>
                    <div>
                        <Header as="h1" style={{ 
                            margin: 0,
                            fontSize: '2.25rem',
                            fontWeight: '700',
                            color: '#062D4D',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.75rem',
                            lineHeight: '1.2'
                        }}>
                            <Icon name="book" style={{ flexShrink: 0, verticalAlign: 'middle' }} />
                            <span>tenantBridge User Manual</span>
                        </Header>
                    </div>
                    {onClose && (
                        <Button 
                            onClick={onClose} 
                            primary
                            size="large"
                            style={{
                                borderRadius: '6px',
                                fontWeight: '600',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                            }}
                        >
                            <Icon name="arrow left" />
                            Back to Application
                        </Button>
                    )}
                </div>

                <Grid>
                    {/* Sidebar Navigation */}
                    <Grid.Column width={4}>
                        <div style={{ 
                            position: 'sticky',
                            top: '90px',
                            background: '#ffffff',
                            borderRadius: '8px',
                            border: '1px solid rgba(0,0,0,0.06)',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                            overflow: 'hidden'
                        }}>
                            <div style={{
                                padding: '1rem 1.25rem',
                                background: 'linear-gradient(135deg, #062D4D 0%, #062D4D 100%)',
                                color: '#ffffff',
                                fontWeight: '600',
                                fontSize: '1.1rem',
                                borderBottom: '1px solid rgba(0,0,0,0.08)',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem'
                            }}>
                                <Icon name="list" />
                                Table of Contents
                            </div>
                            <div style={{ padding: '0.75rem' }}>
                                {sections.map((section, idx) => {
                                    const isActive = activeSection === section.key;
                                    return (
                                        <div
                                            key={section.key}
                                            onClick={() => setActiveSection(section.key)}
                                            style={{
                                                cursor: 'pointer',
                                                padding: '1rem 1.125rem',
                                                margin: '0.35rem 0',
                                                borderRadius: '8px',
                                                background: isActive 
                                                    ? `linear-gradient(135deg, ${section.color === 'blue' ? '#062D4D' : section.color === 'green' ? '#6F9911' : section.color === 'teal' ? '#00b5ad' : section.color === 'orange' ? '#f2711c' : section.color === 'yellow' ? '#daa520' : section.color === 'purple' ? '#a333c8' : section.color === 'violet' ? '#6435c9' : section.color === 'pink' ? '#e03997' : '#767676'}20, ${section.color === 'blue' ? '#062D4D' : section.color === 'green' ? '#6F9911' : section.color === 'teal' ? '#00b5ad' : section.color === 'orange' ? '#f2711c' : section.color === 'yellow' ? '#daa520' : section.color === 'purple' ? '#a333c8' : section.color === 'violet' ? '#6435c9' : section.color === 'pink' ? '#e03997' : '#767676'}10)`
                                                    : '#f8f9fa',
                                                borderLeft: isActive ? `4px solid ${section.color === 'blue' ? '#062D4D' : section.color === 'green' ? '#6F9911' : section.color === 'teal' ? '#00b5ad' : section.color === 'orange' ? '#f2711c' : section.color === 'yellow' ? '#daa520' : section.color === 'purple' ? '#a333c8' : section.color === 'violet' ? '#6435c9' : section.color === 'pink' ? '#e03997' : '#767676'}` : '4px solid transparent',
                                                border: isActive ? `1px solid ${section.color === 'blue' ? '#062D4D' : section.color === 'green' ? '#6F9911' : section.color === 'teal' ? '#00b5ad' : section.color === 'orange' ? '#f2711c' : section.color === 'yellow' ? '#daa520' : section.color === 'purple' ? '#a333c8' : section.color === 'violet' ? '#6435c9' : section.color === 'pink' ? '#e03997' : '#767676'}30` : '1px solid transparent',
                                                color: isActive 
                                                    ? (section.color === 'blue' ? '#062D4D' : section.color === 'green' ? '#6F9911' : section.color === 'teal' ? '#00b5ad' : section.color === 'orange' ? '#f2711c' : section.color === 'yellow' ? '#daa520' : section.color === 'purple' ? '#a333c8' : section.color === 'violet' ? '#6435c9' : section.color === 'pink' ? '#e03997' : '#767676')
                                                    : '#555',
                                                fontWeight: isActive ? '600' : '500',
                                                fontSize: isActive ? '0.95rem' : '0.9rem',
                                                transition: 'all 0.2s ease',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '0.75rem',
                                                boxShadow: isActive ? '0 2px 4px rgba(0,0,0,0.08)' : 'none'
                                            }}
                                            onMouseEnter={(e) => {
                                                if (!isActive) {
                                                    e.currentTarget.style.background = '#f0f4f8';
                                                    e.currentTarget.style.color = '#062D4D';
                                                    e.currentTarget.style.borderLeft = `4px solid ${section.color === 'blue' ? '#062D4D' : section.color === 'green' ? '#6F9911' : section.color === 'teal' ? '#00b5ad' : section.color === 'orange' ? '#f2711c' : section.color === 'yellow' ? '#daa520' : section.color === 'purple' ? '#a333c8' : section.color === 'violet' ? '#6435c9' : section.color === 'pink' ? '#e03997' : '#767676'}40`;
                                                }
                                            }}
                                            onMouseLeave={(e) => {
                                                if (!isActive) {
                                                    e.currentTarget.style.background = '#f8f9fa';
                                                    e.currentTarget.style.color = '#555';
                                                    e.currentTarget.style.borderLeft = '4px solid transparent';
                                                }
                                            }}
                                        >
                                            <Icon 
                                                name={section.icon} 
                                                style={{ 
                                                    fontSize: '1.15em',
                                                    opacity: isActive ? 1 : 0.75,
                                                    flexShrink: 0
                                                }} 
                                            />
                                            <span style={{ flex: 1 }}>{section.label}</span>
                                            {isActive && (
                                                <Icon 
                                                    name="chevron right" 
                                                    style={{ 
                                                        fontSize: '0.9em',
                                                        opacity: 0.6,
                                                        flexShrink: 0
                                                    }} 
                                                />
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </Grid.Column>

                    {/* Main Content */}
                    <Grid.Column width={12}>
                        <div style={{ 
                            padding: '2.5rem',
                            background: '#ffffff',
                            borderRadius: '8px',
                            border: '1px solid rgba(0,0,0,0.06)',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                            minHeight: '600px'
                        }}>
                            {/* Breadcrumb indicator */}
                            <div style={{
                                marginBottom: '1.5rem',
                                padding: '0.75rem 1rem',
                                background: 'linear-gradient(135deg, #f8f9fa 0%, #f0f4f8 100%)',
                                borderRadius: '6px',
                                border: '1px solid rgba(0,0,0,0.06)',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                                fontSize: '0.9rem',
                                color: '#666',
                                lineHeight: '1.5'
                            }}>
                                <Icon 
                                    name="book" 
                                    style={{ 
                                        color: '#062D4D', 
                                        flexShrink: 0, 
                                        margin: 0,
                                        padding: 0,
                                        lineHeight: '1',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }} 
                                />
                                <span style={{ display: 'inline-flex', alignItems: 'center', lineHeight: '1.5' }}>User Manual</span>
                                <Icon 
                                    name="chevron right" 
                                    style={{ 
                                        fontSize: '0.8em', 
                                        opacity: 0.5, 
                                        flexShrink: 0, 
                                        margin: 0,
                                        padding: 0,
                                        lineHeight: '1',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }} 
                                />
                                <span style={{ color: '#062D4D', fontWeight: '600', display: 'inline-flex', alignItems: 'center', lineHeight: '1.5' }}>
                                    {sections.find(s => s.key === activeSection)?.label || 'About Tenant Bridge'}
                                </span>
                            </div>
                            {renderContent()}
                        </div>
                    </Grid.Column>
                </Grid>
            </Container>
        </div>
    );
};

export default AboutAndUserManual;

